/**
 * ConversationInspectorPage · Phase 1 sub-task E
 *
 * Full-page Conversation Inspector. Renders the InspectorPayload returned
 * by GET /api/conversations/{id}/inspector. Mounted at the URL route
 * /analytics/conversations/:id.
 *
 * Layout (per inspector-drawer-vs-fullpage-comparison.html page 4):
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │ Header: ‹ back · ‹ prev · name · next ›  [actions]        │
 *   │ Customer strip + status pills                              │
 *   ├──────────────┬────────────────────────────────────────────┤
 *   │ Transcript    │ Selected span detail (right panel)         │
 *   │ + inline      │ (opens on click; placeholder when nothing  │
 *   │ span trees    │  selected)                                 │
 *   ├──────────────┴────────────────────────────────────────────┤
 *   │ Cross-channel footer (last 7d UCID sessions)              │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Live trace mode (sub-task F) extends this: spans appear in real-time
 * via WebSocket subscription when the conversation is in-flight.
 *
 * Replay Turn (sub-task G) adds a button on assistant messages that
 * opens an overlay with the playground editor.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { conversationInspectorApi, request } from "../../api/client";
import { COLORS } from "../../theme/colors";
import { MarkdownText } from "../../components/MarkdownText";
import { useAuth } from "../../contexts/AuthContext";
import { ReplayTurnOverlay } from "./inspector/ReplayTurnOverlay";
import AnimatedSpanBar from "./inspector/AnimatedSpanBar";
import AddToGoldenSetButton from "./observability/AddToGoldenSetButton";
import { bucketByName, stageHasGenuineError, firstFailedStage } from "./inspectorBucket";

// ─── Color tags for span sources (matches mockup) ────────────────────

const SOURCE_TAG_COLORS = {
  langgraph:  { bg: "#f5f3ff", fg: "#7c3aed", border: "#c4b5fd" },
  llamaindex: { bg: "#f0fdfa", fg: "#0d9488", border: "#99f6e4" },
  openai:     { bg: "#fffbeb", fg: "#d97706", border: "#fde68a" },
  supervisor: { bg: "#fdf2f8", fg: "#be185d", border: "#f9a8d4" },
  asyncpg:    { bg: "#eff6ff", fg: "#2563eb", border: "#bfdbfe" },
  httpx:      { bg: "#eef2ff", fg: "#4f46e5", border: "#c7d2fe" },
  manual:     { bg: "#ecfdf5", fg: "#059669", border: "#a7f3d0" },
};

const VERDICT_PILL = {
  ACCURATE:           { bg: "#ecfdf5", fg: "#047857", label: "accurate" },
  PARTIALLY_ACCURATE: { bg: "#fffbeb", fg: "#b45309", label: "partial" },
  PARTIAL:            { bg: "#fffbeb", fg: "#b45309", label: "partial" },
  INACCURATE:         { bg: "#fff1f2", fg: "#be123c", label: "inaccurate" },
  SKIPPED:            { bg: "#f4f4f5", fg: "#71717a", label: "skipped" },
  UNKNOWN:            { bg: "#f4f4f5", fg: "#71717a", label: "unknown" },
};

const CHANNEL_PILL = {
  webchat:  { bg: "#eff6ff", fg: "#2563eb", label: "web chat" },
  chat:     { bg: "#eff6ff", fg: "#2563eb", label: "web chat" },
  whatsapp: { bg: "#ecfdf5", fg: "#059669", label: "WhatsApp" },
  sms:      { bg: "#fffbeb", fg: "#d97706", label: "SMS" },
  voice:    { bg: "#f5f3ff", fg: "#7c3aed", label: "voice" },
  email:    { bg: "#fffbeb", fg: "#d97706", label: "email" },
  webhook:  { bg: "#f4f4f5", fg: "#52525b", label: "webhook" },
};

// Human-readable party label for a conversation — NEVER a raw id. Falls back
// through the strongest available signal: real name → contact → channel-derived
// label. Eval-harness conversations (no human on the other end) read as such.
const PARTY_BY_CHANNEL = {
  webchat: "Web visitor", chat: "Web visitor", whatsapp: "WhatsApp contact",
  sms: "SMS contact", voice: "Caller", email: "Email contact", webhook: "Webhook client",
};
export function friendlyParty(customer, conversation) {
  if (customer?.name) return customer.name;
  if (customer?.email) return customer.email;
  if (customer?.phone) return customer.phone;
  if (conversation?.channel === "eval") return "Evaluation run";
  return PARTY_BY_CHANNEL[conversation?.channel] || "Anonymous visitor";
}
export function fmtWhen(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined,
    { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// A raw id demoted to a labeled, copyable chip — so ids stay available for
// support/debugging without being shown to users AS the primary label.
export function CopyId({ id, label = "ID" }) {
  const [copied, setCopied] = useState(false);
  if (!id) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        try { navigator.clipboard?.writeText(String(id)); } catch { /* no-op */ }
        setCopied(true); setTimeout(() => setCopied(false), 1200);
      }}
      title={`Copy full ${label}: ${id}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        fontSize: 10.5, color: COLORS.textMuted, background: COLORS.surfaceHover || "#00000010",
        border: `1px solid ${COLORS.border}`, borderRadius: 999, padding: "2px 8px",
        cursor: "pointer", fontFamily: "inherit",
      }}
    >
      <span style={{ opacity: 0.75 }}>{label}</span>
      <span style={{ fontFamily: "monospace" }}>{String(id).slice(0, 8)}</span>
      <span aria-hidden>{copied ? "✓" : "⧉"}</span>
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────

export default function ConversationInspectorPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // App.jsx routes everything through Shell via `/*` catch-all, so
  // useParams() doesn't populate. Parse the URL manually instead.
  const conversationId = location.pathname
    .match(/^\/analytics\/conversations\/([^/?#]+)/)?.[1];

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSpan, setSelectedSpan] = useState(null);  // {turn_index, span}
  const [actionBusy, setActionBusy] = useState(null);      // "tag" | "flag" | "verify"
  // Phase 1 sub-task F · live trace mode
  const [liveEvents, setLiveEvents] = useState([]);
  const [liveStatus, setLiveStatus] = useState("checking");  // checking | live | idle | error

  // ─── Fetch payload ─────────────────────────────────────────────

  const fetchPayload = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await conversationInspectorApi.get(conversationId);
      setPayload(resp.data);
    } catch (e) {
      if (e.status === 404) {
        setError("Conversation not found, or you don't have access to it.");
      } else {
        setError(e.message || "Failed to load conversation.");
      }
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => { fetchPayload(); }, [fetchPayload]);

  // ─── Sub-task F · live trace WebSocket subscription ────────────
  useEffect(() => {
    if (!conversationId) return;
    let ws = null;
    let cancelled = false;

    (async () => {
      // 1. Check if conversation is in-flight (don't open WS for completed ones)
      try {
        // ARC-013: client `request` handles auth header + 401→refresh.
        const { data } = await request(`/api/conversations/${conversationId}/inflight`);
        if (!data?.is_inflight) { setLiveStatus("idle"); return; }
      } catch (e) {
        setLiveStatus("error"); return;
      }
      if (cancelled) return;

      // 2. Open WebSocket
      const proto = location.protocol === "https:" ? "wss:" : "ws:";
      // F (#16): the i2_access cookie authenticates the WS handshake.
      const url = `${proto}//${location.host}/api/conversations/${conversationId}/trace-events`;
      ws = new WebSocket(url);
      ws.onopen = () => setLiveStatus("live");
      ws.onmessage = (ev) => {
        try {
          const event = JSON.parse(ev.data);
          if (event.type === "session.idle" || event.type === "trace.complete") {
            setLiveStatus("idle");
            // Re-fetch payload to load persisted spans from Langfuse
            fetchPayload();
            return;
          }
          setLiveEvents((prev) => [...prev, event].slice(-200));
        } catch (e) { /* ignore malformed */ }
      };
      ws.onerror = () => setLiveStatus("error");
      ws.onclose = () => {
        if (!cancelled) setLiveStatus("idle");
      };
    })();

    return () => {
      cancelled = true;
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [conversationId, fetchPayload]);

  // ─── Action handlers ───────────────────────────────────────────

  const handleFlag = async () => {
    setActionBusy("flag");
    try {
      await conversationInspectorApi.flag(conversationId, {
        queue: "default", notes: "Flagged from Inspector",
      });
      await fetchPayload();
    } catch (e) {
      alert(`Flag failed: ${e.message}`);
    } finally {
      setActionBusy(null);
    }
  };

  const handleVerify = async () => {
    const verdict = window.prompt(
      "Mark verified — verdict (ACCURATE | PARTIAL | INACCURATE):",
      "ACCURATE",
    );
    if (!verdict) return;
    setActionBusy("verify");
    try {
      await conversationInspectorApi.verify(conversationId, verdict);
      await fetchPayload();
    } catch (e) {
      alert(`Verify failed: ${e.message}`);
    } finally {
      setActionBusy(null);
    }
  };

  // ─── Phase B · B4 — per-conv baselines ─────────────────────────
  // Computed client-side from data we already fetched. null when conv
  // has < 3 turns (suppresses delta rendering per B4.3).
  //
  // CRITICAL: this useMemo MUST come before any conditional return —
  // putting it after the early-return on a null payload flips the hook
  // count between renders and triggers React error #310 (Rendered more
  // hooks than during the previous render). The class test
  // test_pipeline_v2_phase_B_hooks_order_class_lock locks the ordering.
  const conversationBaselines = useMemo(
    () => computeConversationBaselines(payload?.turns || []),
    [payload?.turns]
  );

  // ─── Phase D · diff state ────────────────────────────────────────
  // URL state via ?compare=<turn_index> so reload preserves diff view.
  // primaryIdx = which side is the "active" one in the toolbar (T1/T2
  // toggle); also the turn that's rendered in the existing single-turn
  // view BENEATH the diff cards.
  const compareParam = new URLSearchParams(location.search).get("compare");
  const compareTurnIdx = compareParam !== null && !Number.isNaN(Number(compareParam))
    ? Number(compareParam) : null;
  const [primaryIdx, setPrimaryIdx] = useState(0);
  const [diffData, setDiffData] = useState(null);       // {narrative, ...}
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anomalyClusters, setAnomalyClusters] = useState(null);
  const turnsForDiff = payload?.turns || [];
  // Validate compareTurnIdx — gracefully ignored when out of range (D6.2)
  const validCompareIdx = (compareTurnIdx !== null
    && compareTurnIdx >= 0
    && compareTurnIdx < turnsForDiff.length
    && compareTurnIdx !== primaryIdx) ? compareTurnIdx : null;
  // Fetch diff narrative when both turns selected
  useEffect(() => {
    if (validCompareIdx === null) {
      setDiffData(null);
      return;
    }
    setDiffLoading(true);
    setDiffError(null);
    conversationInspectorApi.diffTurns(conversationId, primaryIdx, validCompareIdx)
      .then(setDiffData)
      .catch((e) => setDiffError(String(e?.message || e)))
      .finally(() => setDiffLoading(false));
  }, [validCompareIdx, primaryIdx, conversationId]);
  // Anomaly clusters — lazy-fetched once per page load
  useEffect(() => {
    const agentId = payload?.agent?.id;
    if (!agentId) return;
    conversationInspectorApi.getAnomalyClusters(agentId)
      .then((r) => setAnomalyClusters(Array.isArray(r?.clusters) ? r.clusters : []))
      .catch(() => setAnomalyClusters([]));  // fail open
  }, [payload?.agent?.id]);

  const openCompare = useCallback((targetIdx) => {
    const params = new URLSearchParams(location.search);
    params.set("compare", String(targetIdx));
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setPickerOpen(false);
  }, [location.pathname, location.search, navigate]);
  const closeCompare = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("compare");
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? "?" + qs : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);
  const swapPrimary = useCallback(() => {
    if (validCompareIdx === null) return;
    const newPrimary = validCompareIdx;
    setPrimaryIdx(newPrimary);
    const params = new URLSearchParams(location.search);
    params.set("compare", String(primaryIdx));
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [validCompareIdx, primaryIdx, location.pathname, location.search, navigate]);

  // ─── Loading / error states ────────────────────────────────────

  if (loading) {
    return (
      <Centered>
        <div style={{ color: COLORS.textMuted, fontSize: 14 }}>Loading inspector…</div>
      </Centered>
    );
  }
  if (error) {
    return (
      <Centered>
        <div style={{ color: COLORS.red || "#ef4444", fontSize: 14, marginBottom: 12 }}>{error}</div>
        <button
          onClick={() => navigate("/supervisor")}
          style={btn()}
        >‹ Back to Conversations</button>
      </Centered>
    );
  }
  if (!payload) return null;

  const { conversation, customer, agent, turns = [], cross_channel = [],
          siblings = {}, observability_status, ucid, auth_level } = payload;
  // siblings shape: {in_filter: [{id, channel, status, started_at, csat_score}], total_in_filter: N}
  const siblingsList = Array.isArray(siblings?.in_filter) ? siblings.in_filter : [];
  const siblingsTotal = typeof siblings?.total_in_filter === "number" ? siblings.total_in_filter : 0;

  return (
    <div style={{
      // flex:1 + minWidth:0 — the parent in Shell.jsx is a flex row;
      // without these the inspector shrinks to its intrinsic content
      // width and leaves the rest of the viewport empty (user-reported
      // 2026-06-24). AnalyticsView follows the same pattern.
      flex: 1, minWidth: 0,
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 48px)",   // minus topbar
      background: COLORS.bg,
      overflow: "hidden",
    }}>
      {/* ─── Header bar ─── */}
      <div style={{
        background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
        padding: "10px 16px", display: "flex", alignItems: "center", gap: 12,
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate("/supervisor")}
          style={navLink()}
        >‹ Back to Conversations</button>
        <span style={{ color: COLORS.textMuted }}>|</span>
        <div style={{ flex: 1, fontSize: 13, color: COLORS.textMuted }}>
          {agent?.name || "—"} · {turns.length} turn{turns.length === 1 ? "" : "s"} ·
          {conversation?.started_at &&
            ` started ${new Date(conversation.started_at).toLocaleString()}`}
        </div>
        <button style={btn()} onClick={fetchPayload}>↻ Refresh</button>
        <button style={btn()} onClick={handleFlag} disabled={actionBusy === "flag"}>
          ⚑ Flag
        </button>
        <button style={btn("primary")} onClick={handleVerify} disabled={actionBusy === "verify"}>
          ✓ Mark verified
        </button>
      </div>

      {/* ─── Customer / status strip ─── */}
      <div style={{
        background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
        padding: "12px 16px", flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={friendlyParty(customer, conversation)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>
              {friendlyParty(customer, conversation)}
            </div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span>{customer?.email || customer?.phone || "No contact linked"}</span>
              <CopyId id={conversation?.id} label="Conversation" />
              {ucid && <CopyId id={ucid} label="Cross-channel ID" />}
            </div>
          </div>
          {observability_status === "not_provisioned" && (
            <Pill bg="#fff1f2" fg="#be123c">Langfuse not provisioned for this tenant</Pill>
          )}
          {observability_status === "unavailable" && (
            <Pill bg="#fffbeb" fg="#b45309">Langfuse unavailable · DB-only view</Pill>
          )}
          {/* Sub-task F · live trace indicator */}
          {liveStatus === "live" && (
            <Pill bg="#fef2f2" fg="#b91c1c">
              ● LIVE · {liveEvents.length} events
            </Pill>
          )}
          {liveStatus === "checking" && (
            <Pill bg="#f4f4f5" fg="#52525b">checking live status…</Pill>
          )}
        </div>
        <div style={{ display: "flex", gap: 5, marginTop: 8, flexWrap: "wrap" }}>
          {conversation?.channel && (
            <Pill
              bg={CHANNEL_PILL[conversation.channel]?.bg || "#f4f4f5"}
              fg={CHANNEL_PILL[conversation.channel]?.fg || "#52525b"}
            >{CHANNEL_PILL[conversation.channel]?.label || conversation.channel}</Pill>
          )}
          {conversation?.status && <Pill bg="#ecfdf5" fg="#047857">{conversation.status}</Pill>}
          {conversation?.escalated && <Pill bg="#fdf2f8" fg="#be185d">escalated</Pill>}
          {auth_level > 0 && <Pill bg="#f4f4f5" fg="#52525b">auth L{auth_level}</Pill>}
          {payload.csat && <Pill bg="#ecfdf5" fg="#047857">CSAT {payload.csat}/5</Pill>}
        </div>
        {/* Phase 1.5 #4 · AnimatedSpanBar panel — only renders when there
            ARE in-flight spans. Self-clears when the conversation idles
            (LIVE pill flips to "idle" → events stop arriving → linger
            window expires → panel returns null). */}
        {liveStatus === "live" && (
          /* C6 · live spans nested into staged view per mockup §5.
             Replaces the flat Phase 1.5 #4 LiveSpansPanel. C6.5 — not
             additive (two-panel render would double the bars). */
          <LiveStagedView events={liveEvents} />
        )}
      </div>

      {/* ─── 2-column body: transcript + span detail ─── */}
      <div style={{
        flex: 1, display: "grid",
        gridTemplateColumns: "1fr 380px",
        minHeight: 0, overflow: "hidden",
      }}>
        {/* Transcript */}
        <div style={{ overflow: "auto", padding: "16px 20px", borderRight: `1px solid ${COLORS.border}` }}>
          {/* D1 + D2 + D3 · sibling-turn diff mode */}
          <DiffToolbar
            turns={turns}
            primaryIdx={primaryIdx}
            setPrimaryIdx={setPrimaryIdx}
            compareIdx={validCompareIdx}
            openCompare={openCompare}
            closeCompare={closeCompare}
            swapPrimary={swapPrimary}
            pickerOpen={pickerOpen}
            setPickerOpen={setPickerOpen}
            diffData={diffData}
          />
          {validCompareIdx !== null && (
            <SiblingDiff
              leftTurn={turns[primaryIdx]}
              rightTurn={turns[validCompareIdx]}
              leftIdx={primaryIdx}
              rightIdx={validCompareIdx}
              diffData={diffData}
              diffLoading={diffLoading}
              diffError={diffError}
            />
          )}
          {/* D5 · anomaly badge — shows ABOVE transcript when current
              conversation's agent has matching failure clusters */}
          <AnomalyBadge
            clusters={anomalyClusters}
            turn={turns[primaryIdx]}
          />
          {turns.length === 0 ? (
            <div style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: 40 }}>
              No turns recorded for this conversation.
            </div>
          ) : (
            turns.map((turn, i) => (
              <TurnRow
                key={i}
                turn={turn}
                agentKind={agent?.kind || "regular"}
                conversationId={conversationId}
                agentId={agent?.id}
                baselines={conversationBaselines}
                onSpanClick={(span) => setSelectedSpan({ turn_index: i, span })}
                selectedSpanId={selectedSpan?.span?.id}
              />
            ))
          )}
        </div>

        {/* Right detail panel */}
        <div style={{ background: COLORS.surface, overflow: "auto" }}>
          {selectedSpan ? (
            <SpanDetailPanel turn={selectedSpan} />
          ) : (
            <div style={{ padding: 20, color: COLORS.textMuted, fontSize: 12 }}>
              Click any span in the transcript to see its details.
            </div>
          )}
        </div>
      </div>

      {/* ─── Related conversations footer ───
          Unifies what used to be two separate strips (cross-channel +
          siblings) into one scannable card grid. Cross-channel rows
          tagged with their channel; sibling rows omit the channel chip
          (they share this conv's channel). Click navigates forward.
          Hidden entirely when neither list has entries. */}
      {(cross_channel?.length > 0 || siblingsList?.length > 0) && (
        <RelatedConversationsFooter
          crossChannel={cross_channel || []}
          siblings={siblingsList || []}
          siblingsTotal={siblingsTotal}
          onOpen={(id) => navigate(`/analytics/conversations/${id}`)}
        />
      )}
    </div>
  );
}

// ─── RelatedConversationsFooter ──────────────────────────────────
// Replaces the previous two strips (cross-channel + siblings). One
// section per source, each rendered as a card grid (NOT inline pills)
// so the user can see: channel chip, status color dot, short ID,
// relative date, CSAT — at-a-glance. Click anywhere on a row to open
// that conversation.

const STATUS_COLOR = {
  active: "#10b981", started: "#10b981",
  resolved: "#059669", completed: "#059669", ended: "#059669",
  escalated: "#be185d",
  abandoned: "#d97706",
  unknown: "#71717a",
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const delta = (Date.now() - d.getTime()) / 1000;
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  if (delta < 86400 * 7) return `${Math.floor(delta / 86400)}d ago`;
  return d.toLocaleDateString();
}

export function RelatedRow({ item, showChannel, onClick }) {
  const status = (item.status || "unknown").toLowerCase();
  const statusColor = STATUS_COLOR[status] || STATUS_COLOR.unknown;
  const ch = CHANNEL_PILL[item.channel] || null;
  const when = fmtWhen(item.started_at) || "Conversation";
  return (
    <button
      type="button"
      onClick={() => onClick(item.id)}
      data-testid="related-conv-row"
      title={`Open this conversation (id ${(item.id || "").slice(0, 8)})`}
      style={{
        display: "grid",
        gridTemplateColumns: showChannel ? "8px 78px 118px 1fr auto" : "8px 118px 1fr auto",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 6,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        color: COLORS.text,
      }}
    >
      <span
        title={status}
        style={{
          width: 8, height: 8, borderRadius: 8,
          background: statusColor, display: "inline-block",
        }}
      />
      {showChannel && (
        <span style={{
          background: ch?.bg || "#f4f4f5",
          color: ch?.fg || "#52525b",
          fontSize: 10, fontWeight: 700,
          padding: "2px 6px", borderRadius: 4,
          textAlign: "center",
        }}>
          {ch?.label || item.channel || "unknown"}
        </span>
      )}
      <span style={{ fontSize: 11, color: COLORS.text, fontWeight: 500 }}>
        {when}
      </span>
      <span style={{ fontSize: 11, color: COLORS.text, fontWeight: 500 }}>
        {status}
        {typeof item.csat_score === "number" && (
          <span style={{ color: COLORS.textMuted }}> · CSAT {item.csat_score}/5</span>
        )}
      </span>
      <span style={{ fontSize: 10, color: COLORS.textMuted }}>
        {relTime(item.started_at)}
      </span>
    </button>
  );
}

function RelatedSection({ title, count, items, showChannel, onOpen }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid="related-section" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{
        display: "flex", alignItems: "baseline", gap: 6,
        fontSize: 11, color: COLORS.textMuted, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: 0.4,
      }}>
        <span>{title}</span>
        <span style={{ color: COLORS.textDim, fontWeight: 500 }}>
          {typeof count === "number" && count > items.length
            ? `${items.length} of ${count}`
            : `${items.length}`}
        </span>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 6,
      }}>
        {items.map((it) => (
          <RelatedRow
            key={it.id}
            item={it}
            showChannel={showChannel}
            onClick={onOpen}
          />
        ))}
      </div>
    </div>
  );
}

function RelatedConversationsFooter({ crossChannel, siblings, siblingsTotal, onOpen }) {
  return (
    <div
      data-testid="related-conversations-footer"
      style={{
        // Match the page bg (theme-aware) — `COLORS.surfaceMuted`
        // doesn't exist in the dark theme so the previous fallback
        // `#fafafa` was painting a light-gray strip in dark mode
        // (user-reported 2026-06-24).
        background: COLORS.bg,
        borderTop: `1px solid ${COLORS.border}`,
        padding: "12px 16px 14px",
        display: "flex", flexDirection: "column", gap: 12,
        flexShrink: 0,
      }}
    >
      <RelatedSection
        title="Same customer · other channels"
        items={(crossChannel || []).slice(0, 8)}
        showChannel
        onOpen={onOpen}
      />
      <RelatedSection
        title="Same customer · same channel"
        items={(siblings || []).slice(0, 8)}
        count={siblingsTotal}
        showChannel={false}
        onOpen={onOpen}
      />
    </div>
  );
}


// ─── TurnRow ──────────────────────────────────────────────────────

function TurnRow({ turn, agentKind = "regular", conversationId, onSpanClick, selectedSpanId, agentId, baselines }) {
  const [expanded, setExpanded] = useState(false);
  const [replayOpen, setReplayOpen] = useState(false);
  // Phase C · C3 — chip detail modal state. Lifted here so modals render
  // overlaying the page (not constrained to the StageCard rect).
  const [chipModal, setChipModal] = useState(null);  // {kind, stage, turn} | null
  const [chipBusyKind, setChipBusyKind] = useState(null);
  const [chipToast, setChipToast] = useState(null);  // {text, kind: "ok"|"err"} | null
  const handleChipClick = (kind, ctx) => {
    if (kind === "replay") { setReplayOpen(true); return; }
    setChipModal({ kind, ...ctx });
  };
  const handleChipAction = async (action, payload) => {
    // Wire verdict actions to the new /api/inspector/verification endpoints
    // (backend migration + routes land in the same PR). Until they ship,
    // the action surfaces an inline toast so the user knows the click
    // registered. C8.2 — at least ONE chip per stage proves the wiring
    // isn't decorative.
    const msgId = turn?.assistant_message?.id;
    if (!msgId) {
      setChipToast({ text: "No message_id on this turn — cannot persist.", kind: "err" });
      return;
    }
    setChipBusyKind(action === "flag" ? "verdict_flag" : "verdict_accept");
    try {
      const url = `/api/inspector/verification/by-message/${msgId}/${action}`;
      const body = action === "flag"
        ? { corrected_verdict: payload?.corrected_verdict, note: payload?.note }
        : { note: payload?.note };
      // ARC-013: route through the client `request` helper. This also fixes a
      // latent auth bug — the old raw fetch read the wrong localStorage key
      // ("access_token" instead of "agentic_access_token"), sending an empty
      // Bearer token. The client uses the correct token + 401→refresh contract.
      await request(url, { method: "POST", body: JSON.stringify(body) });
      setChipToast({ text: action === "flag" ? "Verdict flagged + reviewed." : "Verdict accepted + reviewed.", kind: "ok" });
      setChipModal(null);
    } catch (e) {
      const status = e?.status ? `HTTP ${e.status} ` : "";
      setChipToast({ text: `Action failed: ${status}${(e?.message || String(e)).slice(0, 120)}`, kind: "err" });
    } finally {
      setChipBusyKind(null);
    }
  };
  // auto-dismiss toast
  React.useEffect(() => {
    if (!chipToast) return undefined;
    const t = setTimeout(() => setChipToast(null), 4000);
    return () => clearTimeout(t);
  }, [chipToast]);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const verdictStyle = VERDICT_PILL[turn.verdict] || VERDICT_PILL.UNKNOWN;
  // RBAC per design §6: Replay Turn is owner/administrator/developer.
  // business_user + supervisor cannot replay. Backend also enforces via
  // require_administrator dependency on /replay endpoint.
  const canReplay = ["owner", "administrator", "developer"].includes(
    currentUser?.role || ""
  );
  // Replay also needs a Langfuse trace_id on the turn (post-instrumentation
  // conversations only). Pre-instrumentation turns have no trace, no replay.
  const replayable = canReplay && Boolean(turn?.langfuse_trace_id);
  return (
    <div style={{ marginBottom: 12 }}>
      {/* User msg — tinted accent bubble that reads in BOTH light + dark themes.
          Uses COLORS.accentSoft (background) + COLORS.accentText (label) +
          COLORS.text (body) so the bubble inherits whichever theme is active.
          Pre-fix used hardcoded #eff6ff / #1e3a8a / #27272a → invisible in dark. */}
      {turn.user_message?.content && (
        <div style={{
          background: COLORS.accentSoft || COLORS.surface,
          border: `1px solid ${COLORS.accent || COLORS.border}`, borderRadius: 6,
          padding: "8px 10px", marginBottom: 6, fontSize: 13, color: COLORS.text,
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.accentText || COLORS.textMuted,
                        textTransform: "uppercase", marginBottom: 3 }}>
            customer · T{turn.turn_index + 1}
            {turn.started_at && ` · ${new Date(turn.started_at).toLocaleTimeString()}`}
          </div>
          {turn.user_message.content}
        </div>
      )}
      {/* Assistant msg */}
      {turn.assistant_message?.content && (
        <div style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, padding: "8px 10px", fontSize: 13, color: COLORS.text,
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted,
                        textTransform: "uppercase", marginBottom: 4,
                        display: "flex", alignItems: "center", gap: 6 }}>
            assistant
            {turn.duration_ms && (
              <span style={{ color: COLORS.textMuted }}>
                · {(turn.duration_ms / 1000).toFixed(2)}s
              </span>
            )}
            {/* Only render verdict pill when we have a real verdict.
                "UNKNOWN" was misleading — it implied "verified but verdict
                unknown" when the truth is "not verified at all". For
                turns with no verification_log join, render a subtle
                "not verified" affordance so the field is still
                debuggable but not pretending to be a verdict. */}
            {turn.verdict && turn.verdict !== "UNKNOWN" ? (
              <Pill bg={verdictStyle.bg} fg={verdictStyle.fg}>
                {verdictStyle.label}
              </Pill>
            ) : (
              <span
                title="Fact-check verdict not available (fact_checker disabled, learn-only, or pre-fix conversation)"
                style={{ fontSize: 10, color: COLORS.textMuted, fontStyle: "italic" }}
              >
                not verified
              </span>
            )}
            {/* Affordance row — ALWAYS render both slots so the layout
                stays consistent across turns. Disabled / muted states
                replace conditional omission so the user knows whether
                debugging IS possible vs whether the data wasn't captured.
                User-reported 2026-06-24: turns with no spans / no trace
                used to silently drop the buttons, making it look like a
                bug. */}
            <div style={{
              marginLeft: "auto",
              display: "flex", gap: 10, alignItems: "center",
            }}>
              {turn.spans && turn.spans.length > 0 ? (
                <button
                  onClick={() => setExpanded(!expanded)}
                  style={{
                    background: "none", border: "none",
                    color: COLORS.accentText || COLORS.accent || "#2563eb",
                    cursor: "pointer", fontSize: 10, fontWeight: 600,
                  }}
                >
                  {expanded ? "▴ hide pipeline" : "▾ show pipeline"} ({turn.spans.length} spans)
                </button>
              ) : (
                <span
                  title="No spans captured — either fact_check / call_llm bypassed (fast-path domain reject, early guardrail) or the trace was lost."
                  style={{ fontSize: 10, color: COLORS.textMuted, fontStyle: "italic" }}
                >
                  no spans
                </span>
              )}
              {/* Replay Turn — admin only + turn must have a Langfuse trace.
                  Phase 1.5 item #1; opens 3-column overlay with editor +
                  original + replayed. Backend wired in Phase 1 sub-task G. */}
              {replayable ? (
                <button
                  onClick={() => setReplayOpen(true)}
                  style={{
                    background: "none", border: "none",
                    color: COLORS.accentText || COLORS.accent || "#2563eb",
                    cursor: "pointer", fontSize: 10, fontWeight: 600,
                  }}
                  title="Edit system prompt + re-run against same tools (admin only)"
                >
                  ↻ replay turn
                </button>
              ) : (
                <span
                  title={
                    !turn.langfuse_trace_id
                      ? "No Langfuse trace recorded for this turn — replay needs a trace to re-run against."
                      : "Replay requires owner / administrator / developer role."
                  }
                  style={{ fontSize: 10, color: COLORS.textMuted, fontStyle: "italic" }}
                >
                  {!turn.langfuse_trace_id ? "no trace" : "replay locked"}
                </span>
              )}
              {/* D1 · capture this turn as a golden-set item (eval ground
                  truth). Self-contained + tested in AddToGoldenSetButton. */}
              {turn.langfuse_trace_id && (
                <AddToGoldenSetButton
                  traceId={turn.langfuse_trace_id}
                  defaultExpected={turn.assistant_message?.content || ""}
                />
              )}
            </div>
          </div>
          {/* OBS-2: render markdown (bold/lists/headings) like the chat does,
              instead of printing raw ** ** / - source. Same MarkdownText the
              customer sees; the transcript should match what the user saw. */}
          <div style={{ lineHeight: 1.55 }}>
            <MarkdownText text={turn.assistant_message.content} />
          </div>
          {turn.citations && turn.citations.length > 0 && (
            <div
              style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}
              data-testid="turn-citation-pills"
            >
              {turn.citations.map((c) => {
                // Phase 1.5 #7 · Cited-in forward placeholder.
                // Each citation pill links forward to the per-agent
                // library route at /agents/{agent_id}/library?citation={c}.
                // The library page itself is a Phase 2 deliverable — the
                // route 404s today, but the pill becomes interactive +
                // discoverable so power users can grep server logs for
                // the citation reference. Without agentId we render the
                // legacy non-clickable span (backwards-compat for older
                // tools rendering this component without the prop).
                if (!agentId) {
                  return (
                    <span
                      key={c}
                      data-testid="citation-pill-static"
                      style={{
                        background: COLORS.accentSoft || COLORS.surface,
                        color: COLORS.accentText || COLORS.text,
                        padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600,
                      }}
                    >[{c}]</span>
                  );
                }
                const target = `/agents/${agentId}/library?citation=${encodeURIComponent(c)}`;
                return (
                  <button
                    key={c}
                    type="button"
                    data-testid="citation-pill"
                    onClick={() => navigate(target)}
                    title={`Open source [${c}] in agent library (Phase 2 destination)`}
                    style={{
                      background: COLORS.accentSoft || COLORS.surface,
                      color: COLORS.accentText || COLORS.text,
                      padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600,
                      border: `1px solid ${(COLORS.accent || "#7c3aed")}40`,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    [{c}]
                  </button>
                );
              })}
            </div>
          )}
          {/* Pipeline view (Phase 1.5) — narrative story of how the turn
              was processed, instead of a flat 100+ span dump. Mode-aware:
              supervisor renders L1/L1.5/L2/L3 stages with per-expert
              fan-out; regular renders 6 chat-pipeline stages. ▾ Raw
              timeline toggle inside falls back to the original flat tree
              for engineer-debug-mode. */}
          {expanded && turn.spans && turn.spans.length > 0 && (
            <PipelineView
              spans={turn.spans}
              agentKind={agentKind}
              turn={turn}
              baselines={baselines}
              onSpanClick={onSpanClick}
              selectedSpanId={selectedSpanId}
              onChipClick={handleChipClick}
            />
          )}
        </div>
      )}
      {/* Replay overlay — modal portal-style, conditionally mounted */}
      {replayOpen && (
        <ReplayTurnOverlay
          conversationId={conversationId}
          turn={turn}
          onClose={() => setReplayOpen(false)}
        />
      )}
      {/* C3 · Chip detail modal */}
      <ChipDetailModal
        active={chipModal}
        agentId={agentId}
        busyKind={chipBusyKind}
        onAction={handleChipAction}
        onClose={() => setChipModal(null)}
      />
      {/* C3.8 · transient toast for action results */}
      {chipToast && (
        <div
          data-testid="chip-toast"
          style={{
            position: "fixed", right: 18, bottom: 18, zIndex: 60,
            background: chipToast.kind === "ok" ? "#059669" : "#dc2626",
            color: "#fff", padding: "8px 12px", borderRadius: 6,
            fontSize: 12, fontWeight: 600, maxWidth: 360,
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          }}
        >
          {chipToast.text}
        </div>
      )}
    </div>
  );
}


// ─── PipelineView ──────────────────────────────────────────────────
//
// Narrative renderer for a turn's spans. Buckets the raw OTEL spans
// into pipeline STAGES (e.g. routing → context → LLM → tools → safety
// → output) so the operator sees a story, not a 100+ span dump.
//
// Mode-aware:
//   - regular   → 6 chat-pipeline stages (single-thread)
//   - supervisor → L1/L1.5/L2/L3-fanout/L3-synthesis/safety/output,
//                  with L3 stage showing per-expert sub-pipelines
//
// Raw timeline preserved under a "▾ Raw" toggle for engineer debug.

// ─── Phase C · ChipDetailModal ────────────────────────────────────────
//
// Centralized inline modal for every chip's "see X" / "accept Y" action.
// Receives an active = {kind, stage, turn, agentId, conversationId} payload
// or null to close. Owner handles backend-state mutations and toast errors.

function ChipDetailModal({ active, onClose, onAction, busyKind, agentId }) {
  if (!active) return null;
  const { kind, stage, turn } = active;
  const titles = {
    guardrail_config: "Input guardrails configuration",
    chunks: "Retrieved RAG chunks",
    mcp_tools: "MCP tools loaded for this turn",
    message_stack: "Full message stack sent to the LLM",
    tool_definitions: "Tool definitions registered for this turn",
    tool_io: "Per-tool input / output",
    verdict_accept: "Accept fact-check verdict",
    verdict_flag: "Flag verdict as incorrect",
    verification_row: "verification_log row",
    synthesis_prompt: "Synthesis LLM call input",
    expert_outputs: "Per-expert outputs (side-by-side)",
  };
  const title = titles[kind] || kind;
  return (
    <div
      onClick={onClose}
      data-testid="chip-detail-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          borderRadius: 8, padding: 16, width: "min(720px, 92vw)",
          maxHeight: "80vh", overflow: "auto", color: COLORS.text,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>
              {stage?.title || "stage"}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{title}</div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: COLORS.textMuted,
            cursor: "pointer", fontSize: 16, padding: 4,
          }}>✕</button>
        </div>
        <ChipDetailBody kind={kind} stage={stage} turn={turn} agentId={agentId} onAction={onAction} busyKind={busyKind} />
      </div>
    </div>
  );
}

function _ModalPre({ value }) {
  return (
    <pre style={{
      background: COLORS.bg, border: `1px solid ${COLORS.border}`,
      borderRadius: 4, padding: 10, fontSize: 11, lineHeight: 1.55,
      whiteSpace: "pre-wrap", wordBreak: "break-word",
      color: COLORS.text, fontFamily: "monospace",
      maxHeight: "55vh", overflow: "auto",
    }}>
      {value}
    </pre>
  );
}

function _ModalEmpty({ label }) {
  return (
    <div style={{ fontSize: 12, color: COLORS.textMuted, fontStyle: "italic", padding: 12, textAlign: "center" }}>
      {label || "No data captured for this turn."}
    </div>
  );
}

function _extractToolSpans(stage) {
  const spans = stage?.spans || [];
  return spans.filter(s => {
    const n = (s.name || "").toLowerCase();
    return n.startsWith("post") || n.startsWith("get")
      || /^tool\.|^mcp\.|^agentgateway/.test(n);
  });
}

function _extractMessageStack(stage) {
  const spans = stage?.spans || [];
  for (const s of spans) {
    const a = s.attributes || {};
    for (const k of ["messages", "request.messages", "llm.request.messages", "input", "prompt"]) {
      if (a[k]) return a[k];
    }
  }
  return null;
}

function ChipDetailBody({ kind, stage, turn, agentId, onAction, busyKind }) {
  if (kind === "chunks") {
    const chunks = Array.isArray(turn?.retrieved_chunks) ? turn.retrieved_chunks : [];
    if (chunks.length === 0) return <_ModalEmpty label="No RAG chunks were retrieved on this turn." />;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {chunks.map((c, i) => (
          <div key={i} style={{
            background: COLORS.bg, border: `1px solid ${COLORS.border}`,
            borderRadius: 4, padding: 8,
          }}>
            <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 4 }}>
              chunk {i + 1}{c.source ? ` · ${c.source}` : ""}{c.score ? ` · score ${c.score.toFixed?.(3) || c.score}` : ""}
            </div>
            <div style={{ fontSize: 12, color: COLORS.text }}>
              {String(c.content || c.text || JSON.stringify(c)).slice(0, 600)}
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "mcp_tools") {
    const branches = detectParallelBranches(stage);
    const mcp = branches.find(b => b.label === "MCP discovery");
    if (!mcp) return <_ModalEmpty label="MCP discovery did not run on this turn." />;
    const attrs = mcp.raw?.attributes || {};
    return (
      <div>
        <_ModalPre value={JSON.stringify(attrs, null, 2)} />
        {agentId && (
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 8 }}>
            Live tool catalog: <code>GET /api/agents/{agentId}/capability</code>
          </div>
        )}
      </div>
    );
  }
  if (kind === "guardrail_config") {
    return <_ModalEmpty label="Input guardrails config lives on the agent record. Open the Agent Detail Viewer → Wiring tab for the editable config." />;
  }
  if (kind === "message_stack") {
    const msgs = _extractMessageStack(stage);
    if (!msgs) return <_ModalEmpty label="Message stack was not captured in span attributes for this turn. (Pre-fix conversations don't have this; turn captured after the message-stack telemetry lands will populate.)" />;
    return <_ModalPre value={typeof msgs === "string" ? msgs : JSON.stringify(msgs, null, 2)} />;
  }
  if (kind === "tool_definitions") {
    const tools = _extractToolSpans(stage);
    if (tools.length === 0) return <_ModalEmpty label="No tool calls fired on this turn." />;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {tools.map((t, i) => (
          <div key={t.id || i} style={{
            background: COLORS.bg, border: `1px solid ${COLORS.border}`,
            borderRadius: 4, padding: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
              {t.attributes?.tool_name || t.name}
            </div>
            <_ModalPre value={JSON.stringify(t.attributes?.tool_definition || t.attributes || {}, null, 2)} />
          </div>
        ))}
      </div>
    );
  }
  if (kind === "tool_io") {
    const tools = _extractToolSpans(stage);
    if (tools.length === 0) return <_ModalEmpty label="No tool calls fired on this turn." />;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {tools.map((t, i) => {
          const a = t.attributes || {};
          const input = a.input || a["http.request.body"] || a.tool_arguments || a.arguments;
          const output = a.output || a["http.response.body"] || a.tool_result || a.result;
          return (
            <div key={t.id || i} style={{
              background: COLORS.bg, border: `1px solid ${COLORS.border}`,
              borderRadius: 4, padding: 8,
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
                {a.tool_name || t.name} · {formatDur(t.duration_ms)}
                {t.status === "ERROR" && (
                  <span style={{ color: "#dc2626", marginLeft: 8 }}>ERROR</span>
                )}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 2 }}>input</div>
              <_ModalPre value={input ? (typeof input === "string" ? input : JSON.stringify(input, null, 2)) : "(not captured)"} />
              <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 6, marginBottom: 2 }}>output</div>
              <_ModalPre value={output ? (typeof output === "string" ? output : JSON.stringify(output, null, 2)) : "(not captured)"} />
            </div>
          );
        })}
      </div>
    );
  }
  if (kind === "verdict_accept" || kind === "verdict_flag") {
    const [note, setNote] = React.useState("");
    const [corrected, setCorrected] = React.useState(turn?.verdict || "ACCURATE");
    const isFlag = kind === "verdict_flag";
    return (
      <div>
        <div style={{ fontSize: 12, color: COLORS.text, marginBottom: 8 }}>
          Current verdict: <strong>{turn?.verdict || "—"}</strong>
        </div>
        {isFlag && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Corrected verdict</div>
            <select
              value={corrected}
              onChange={(e) => setCorrected(e.target.value)}
              style={{
                background: COLORS.bg, color: COLORS.text,
                border: `1px solid ${COLORS.border}`, borderRadius: 4,
                padding: "4px 8px", fontSize: 12,
              }}
            >
              <option value="ACCURATE">ACCURATE</option>
              <option value="PARTIALLY_ACCURATE">PARTIALLY_ACCURATE</option>
              <option value="INACCURATE">INACCURATE</option>
            </select>
          </div>
        )}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Note (optional)</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            style={{
              width: "100%", background: COLORS.bg, color: COLORS.text,
              border: `1px solid ${COLORS.border}`, borderRadius: 4,
              padding: 8, fontSize: 12, fontFamily: "inherit",
            }}
          />
        </div>
        <button
          onClick={() => onAction?.(isFlag ? "flag" : "accept", { note, corrected_verdict: isFlag ? corrected : undefined })}
          disabled={busyKind === kind}
          style={{
            background: isFlag ? "#dc2626" : "#10b981", color: "#fff",
            border: "none", borderRadius: 4, padding: "6px 14px",
            fontSize: 12, fontWeight: 600, cursor: busyKind === kind ? "wait" : "pointer",
          }}
        >
          {busyKind === kind ? "Saving…" : (isFlag ? "Flag as incorrect" : "Accept verdict")}
        </button>
      </div>
    );
  }
  if (kind === "verification_row") {
    return (
      <_ModalPre value={JSON.stringify({
        verdict: turn?.verdict,
        re_prompt_count: turn?.re_prompt_count,
        unsupported_claims: turn?.unsupported_claims,
        reward: turn?.reward,
      }, null, 2)} />
    );
  }
  if (kind === "synthesis_prompt") {
    const synthesisSpans = stage?._synthesisSpans || [];
    if (synthesisSpans.length === 0) return <_ModalEmpty label="Synthesis didn't run on this turn." />;
    // Pull the input/messages from the synthesis span attrs
    const head = synthesisSpans[0] || {};
    const attrs = head.attributes || {};
    const prompt = attrs.synthesis_prompt
                 || attrs.input
                 || attrs["llm.request.messages"]
                 || attrs.messages
                 || null;
    if (!prompt) {
      return <_ModalEmpty label="Synthesis prompt was not captured in span attributes. (Pre-fix conversations; attribute will populate after the synthesis-prompt telemetry lands.)" />;
    }
    return <_ModalPre value={typeof prompt === "string" ? prompt : JSON.stringify(prompt, null, 2)} />;
  }
  if (kind === "expert_outputs") {
    const experts = stage?._experts || [];
    if (experts.length === 0) return <_ModalEmpty label="No expert outputs on this turn." />;
    return (
      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(${Math.min(experts.length, 3)}, 1fr)`,
        gap: 8,
      }}>
        {experts.map(exp => {
          // Pull the expert's output from the last span attrs
          const last = exp.spans[exp.spans.length - 1] || {};
          const a = last.attributes || {};
          const out = a.output
                   || a.reply
                   || a["llm.response.content"]
                   || a.result;
          return (
            <div key={exp.id}
              style={{
                background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                borderRadius: 4, padding: 8, minWidth: 0,
              }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: COLORS.text, marginBottom: 4,
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <span>{exp.icon || "•"}</span>
                <span>{exp.name}</span>
                <span style={{
                  fontSize: 9, color: exp.status === "dropped" ? "#dc2626" : "#10b981",
                  textTransform: "uppercase", letterSpacing: 0.4, marginLeft: "auto",
                }}>
                  {exp.status === "dropped" ? "DROPPED" : "OK"}
                </span>
              </div>
              {out ? (
                <_ModalPre value={typeof out === "string" ? out : JSON.stringify(out, null, 2)} />
              ) : (
                <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: "italic" }}>
                  (output not captured)
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  return <_ModalEmpty label={`Action "${kind}" has no body renderer yet.`} />;
}


// ─── Phase C · C6 LiveStagedView ──────────────────────────────────────
//
// Replaces the Phase 1.5 #4 flat LiveSpansPanel. Buckets the raw live
// event stream into the same 6 stages used by Phase A bucketing and
// renders RUNNING / PENDING / OK pills per stage. AnimatedSpanBar nests
// per stage row so the operator sees the conversation unfolding in
// semantic terms — not as a raw waterfall.
//
// C6.5 — not additive with the flat panel; this is the only live render.

const _STAGE_DEFS = [
  { id: "routing",       title: "Routing",          stepNumber: "1" },
  { id: "input_safety",  title: "Input Guardrails", stepNumber: "2" },
  { id: "context",       title: "Context Loading",  stepNumber: "3" },
  { id: "llm",           title: "LLM Reasoning",    stepNumber: "4" },
  { id: "tools",         title: "Tool Execution",   stepNumber: "5" },
  { id: "output_safety", title: "Output Safety",    stepNumber: "6" },
];

function _bucketLiveEventToStage(name) {
  const n = (name || "").trim();
  // Direct LangGraph node match (most common — events carry node names)
  if (NODE_TO_BUCKET.has(n)) return NODE_TO_BUCKET.get(n);
  // Heuristic
  return bucketByName(n) || null;
}

export function LiveStagedView({ events }) {
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  // PER-013: the 10Hz tick only exists to animate running bars. Gate it on
  // whether any span is still running so an idle/finished trace doesn't
  // re-render at 10Hz forever.
  const hasRunning = React.useMemo(
    () => (events || []).some(ev => ev && ev.type === "span.start" && !(events || []).some(
      e => e && e.type === "span.end" && e.span_id === ev.span_id
    )),
    [events]
  );
  React.useEffect(() => {
    if (!hasRunning) return undefined;  // nothing animating → no interval
    // 10Hz tick for running-bar animations — mirrors LiveSpansPanel's pattern
    const id = setInterval(() => setNowMs(Date.now()), 100);
    return () => clearInterval(id);
  }, [hasRunning]);
  // Phase A bucket logic, applied to live events. Map<stage_id, Span[]>
  const liveByStage = React.useMemo(() => {
    const m = new Map();
    for (const def of _STAGE_DEFS) m.set(def.id, []);
    const records = new Map();  // span_id → record
    let baseT = null;
    for (const ev of events || []) {
      if (!ev || !ev.span_id) continue;
      const t = ev.started_at ? Date.parse(ev.started_at) : null;
      if (baseT === null && t && Number.isFinite(t)) baseT = t;
      if (ev.type === "span.start") {
        records.set(ev.span_id, {
          id: ev.span_id, name: ev.name || "(unnamed)",
          status: "running",
          startedAtMs: (t && Number.isFinite(t)) ? t : (baseT || 0),
          durationMs: null, endedAtMs: null,
        });
      } else if (ev.type === "span.end") {
        const prev = records.get(ev.span_id);
        const endT = ev.ended_at ? Date.parse(ev.ended_at) : null;
        const dur = ev.duration_ms ?? (prev && endT ? endT - prev.startedAtMs : 0);
        if (prev) {
          prev.status = ev.status || "OK";
          prev.durationMs = dur;
          prev.endedAtMs = endT || (prev.startedAtMs + dur);
        } else {
          records.set(ev.span_id, {
            id: ev.span_id, name: ev.name || "(unnamed)",
            status: ev.status || "OK",
            startedAtMs: (endT || 0) - (dur || 0),
            durationMs: dur, endedAtMs: endT || 0,
          });
        }
      }
    }
    for (const r of records.values()) {
      const bucket = _bucketLiveEventToStage(r.name);
      if (bucket && m.has(bucket)) m.get(bucket).push(r);
    }
    return m;
  }, [events]);

  // Find the "first running stage" — the rest are PENDING (per mockup §5).
  const stageStatus = (stageId) => {
    const recs = liveByStage.get(stageId) || [];
    if (recs.length === 0) return "pending";
    if (recs.some(r => r.status === "running")) return "running";
    if (recs.some(r => r.status === "ERROR")) return "error";
    return "ok";
  };
  // Compute max duration for shared scale
  const maxDurationMs = React.useMemo(() => {
    let mx = 2000;
    for (const recs of liveByStage.values()) {
      for (const r of recs) {
        const d = r.durationMs ?? 0;
        if (d > mx) mx = d;
      }
    }
    return Math.min(30000, mx);
  }, [liveByStage]);


  return (
    <div
      data-testid="live-staged-view"
      style={{
        marginTop: 10, padding: 8, background: COLORS.surface,
        border: `1px solid ${COLORS.border}`, borderRadius: 6,
      }}
    >
      <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textDim || COLORS.textMuted,
                    textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
        live pipeline · {Array.from(liveByStage.values()).reduce((a, recs) => a + recs.filter(r => r.status === "running").length, 0)} running
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {_STAGE_DEFS.map(def => {
          const status = stageStatus(def.id);
          const recs = liveByStage.get(def.id) || [];
          const accentBar = STAGE_COLOR[def.id] || COLORS.accent || "#7c3aed";
          const pill = status === "running"
            ? { label: "RUNNING", bg: COLORS.accentSoft || "rgba(124,58,237,0.18)", fg: COLORS.accentText || "#a78bfa" }
            : status === "pending"
              ? { label: "PENDING", bg: "rgba(255,255,255,0.05)", fg: COLORS.textMuted }
              : status === "error"
                ? { label: "FAILED", bg: "rgba(220,38,38,0.18)", fg: "#dc2626" }
                : { label: "OK", bg: "rgba(16,185,129,0.18)", fg: "#059669" };
          const opacity = status === "pending" ? 0.5 : 1;
          return (
            <div
              key={def.id}
              data-testid={`live-stage-${def.id}`}
              data-status={status}
              style={{
                background: COLORS.surfaceMuted || "transparent",
                border: `1px solid ${COLORS.border}`,
                borderLeft: `3px solid ${accentBar}`,
                borderRadius: 4, padding: "5px 8px", opacity,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted,
                              textTransform: "uppercase", minWidth: 18 }}>
                  {def.stepNumber}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700,
                              color: status === "pending" ? COLORS.textMuted : COLORS.text }}>
                  {def.title}
                </span>
                <span data-testid={`live-pill-${def.id}`}
                  style={{
                    fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8,
                    background: pill.bg, color: pill.fg, letterSpacing: 0.3,
                  }}>
                  {pill.label}
                </span>
              </div>
              {recs.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {recs.slice(0, 6).map(r => (
                    <AnimatedSpanBar
                      key={r.id}
                      name={r.name}
                      status={r.status}
                      startedAtMs={r.startedAtMs}
                      durationMs={r.durationMs}
                      maxDurationMs={maxDurationMs}
                      nowMs={nowMs}
                    />
                  ))}
                  {recs.length > 6 && (
                    <div style={{ fontSize: 9, color: COLORS.textMuted, fontStyle: "italic" }}>
                      …{recs.length - 6} more in {def.title}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function PipelineView({ spans, agentKind, turn, baselines, onSpanClick, selectedSpanId, onChipClick }) {
  const [showRaw, setShowRaw] = React.useState(false);
  const isSupervisor = agentKind === "supervisor";
  const stages = React.useMemo(
    () => (isSupervisor ? bucketSpansForSupervisor(spans) : bucketSpansForChat(spans)),
    [spans, isSupervisor]
  );
  // Phase C · C4 — failure banner above the pipeline when any stage is FAILED
  const failure = React.useMemo(
    () => (isSupervisor ? null : computeFailureBanner(stages, turn)),
    [stages, turn, isSupervisor]
  );
  return (
    <div style={{
      marginTop: 8,
      background: COLORS.surfaceMuted || COLORS.bg,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 5, padding: "8px 10px",
    }}>
      {/* Header strip */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 6, paddingBottom: 4, borderBottom: `1px solid ${COLORS.border}`,
      }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted,
                       textTransform: "uppercase", letterSpacing: 0.4 }}>
          {isSupervisor ? "supervisor pipeline" : "pipeline"}
        </span>
        <button
          onClick={() => setShowRaw(!showRaw)}
          style={{
            background: "none", border: "none",
            color: COLORS.accentText || COLORS.accent || "#2563eb",
            cursor: "pointer", fontSize: 10, fontWeight: 600,
          }}
        >
          {showRaw ? "▴ pipeline view" : "▾ raw timeline"}
        </button>
      </div>
      {/* C4 · failure banner — frames the turn around the failure point
          when at least one stage's pill is FAILED. */}
      {failure && !showRaw && (
        <div
          data-testid="pipeline-failure-banner"
          style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            background: "rgba(220,38,38,0.10)",
            border: `1px solid rgba(220,38,38,0.35)`,
            borderLeft: `3px solid #dc2626`,
            borderRadius: 6, padding: "8px 10px", marginBottom: 8,
          }}
        >
          <div style={{
            width: 22, height: 22, borderRadius: "50%",
            background: "#dc2626", color: "#fff",
            fontSize: 12, fontWeight: 700, lineHeight: "22px",
            textAlign: "center", flexShrink: 0,
          }}>!</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626" }}>
              Turn failed at {failure.stageTitle} · {failure.pillLabel}.
            </div>
            <div style={{ fontSize: 11, color: COLORS.text, marginTop: 2 }}>
              {failure.cause}
            </div>
            <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>
              {failure.recovery}
            </div>
          </div>
        </div>
      )}
      {showRaw ? (
        <RawSpanTree spans={spans} onSpanClick={onSpanClick} selectedSpanId={selectedSpanId} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {stages.map((stage, i) => (
            <StageCard
              key={stage.id || i}
              stage={stage}
              turn={turn}
              baselines={baselines}
              onSpanClick={onSpanClick}
              selectedSpanId={selectedSpanId}
              onChipClick={onChipClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RawSpanTree({ spans, onSpanClick, selectedSpanId }) {
  return (
    <div>
      {flattenSpansForTree(spans).map((s) => {
        const color = SOURCE_TAG_COLORS[s.source_tag] || SOURCE_TAG_COLORS.manual;
        const isSel = s.id === selectedSpanId;
        return (
          <div
            key={s.id}
            onClick={() => onSpanClick(s)}
            style={{
              display: "grid",
              gridTemplateColumns: "12px 1fr 60px",
              gap: 6, padding: "3px 6px", alignItems: "center",
              fontSize: 11, cursor: "pointer",
              paddingLeft: 6 + (s.depth || 0) * 12,
              background: isSel ? (COLORS.accentSoft || COLORS.surface) : "transparent",
              borderLeft: isSel ? `2px solid ${COLORS.accent || "#7c3aed"}` : "2px solid transparent",
              borderRadius: 3,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: 2, background: color.fg }} />
            <span style={{ color: COLORS.text, fontWeight: 600,
                           overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {s.name}
            </span>
            <span style={{ textAlign: "right", fontFamily: "monospace",
                           color: COLORS.textMuted, fontSize: 10 }}>
              {s.duration_ms !== null && s.duration_ms !== undefined ? formatDur(s.duration_ms) : "—"}
            </span>
          </div>
        );
      })}
    </div>
  );
}


// ─── StageCard ─────────────────────────────────────────────────────
// Renders one stage row: title + duration + auto-summary line.
// Click to expand → sub-spans listed (re-uses the click-to-detail flow).
// For supervisor L3 fan-out, child experts render as nested ExpertCards.

function StageCard({ stage, turn, baselines, onSpanClick, selectedSpanId, onChipClick, depth = 0 }) {
  const [open, setOpen] = React.useState(false);
  if (!stage) return null;
  // "Empty" means no spans + no experts + no MEANINGFUL summary values.
  // makeStage always emits a summary object (often with undefined values)
  // so checking `!stage.summary` doesn't work — check the values too.
  const hasSummaryValues = stage.summary
    && Object.values(stage.summary).some(v => v !== undefined && v !== null && v !== "");
  const isEmpty = (!stage.spans?.length && !stage.experts?.length && !hasSummaryValues);
  // Phase A · A1.3 AMENDED — render empty NAMED stages as muted skeletons
  // so the 6-step graph structure stays visible at a glance. "Other"
  // (the noise-catcher) still hides when empty — it's not a graph node.
  if (isEmpty && stage.id === "other") return null;
  if (isEmpty) {
    // Muted skeleton for empty named stages
    const accentBarEmpty = STAGE_COLOR[stage.id] || COLORS.border;
    return (
      <div
        data-testid={`pipeline-stage-${stage.id}`}
        style={{
          background: COLORS.surface, borderRadius: 4,
          border: `1px solid ${COLORS.border}`,
          borderLeft: `3px solid ${accentBarEmpty}`,
          padding: "6px 9px",
          marginLeft: depth * 16,
          opacity: 0.45,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted,
                        textTransform: "uppercase", letterSpacing: 0.4, minWidth: 22 }}>
            {stage.stepNumber || ""}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, flex: 1 }}>
            {stage.title}
          </span>
          <span style={{
            fontSize: 9, padding: "1px 7px", borderRadius: 10,
            background: "rgba(255,255,255,0.04)", color: COLORS.textMuted,
            fontStyle: "italic",
          }}>
            did not run
          </span>
        </div>
      </div>
    );
  }
  // Phase B · B1/B2/B3 — narrative + pill + delta
  const narrativeParts = computeStageNarrative(stage, turn);
  const pill = computeStagePill(stage, turn, baselines);
  const delta = _deltaFor(stage.durationMs, baselines?.[stage.id]);
  // Phase C · C1/C2/C4 — parallel branches + loop pill + failed modifier
  const branches = detectParallelBranches(stage);
  const loop = computeLoopMetrics(stage, turn);
  const isFailed = pill && pill.label === "FAILED";
  const titleColor = (isFailed || stage.status === "error")
    ? "#dc2626"
    : stage.status === "warn" ? "#d97706" : COLORS.text;
  // C4.7 — failed stage gets red left border that overrides the stage color
  const accentBar = isFailed ? "#dc2626" : (STAGE_COLOR[stage.id] || COLORS.accent || "#7c3aed");
  const deltaColor = delta?.tier === "bad" ? "#dc2626"
                   : delta?.tier === "up" ? "#d97706"
                   : delta?.tier === "good" ? "#059669"
                   : null;
  const partStyle = (style) => {
    switch (style) {
      case "bold": return { color: COLORS.text, fontWeight: 600 };
      case "bad":  return { color: "#dc2626", fontWeight: 600 };
      case "up":   return { color: "#d97706", fontWeight: 600 };
      case "good": return { color: "#059669", fontWeight: 600 };
      case "muted-italic": return { color: COLORS.textMuted, fontStyle: "italic" };
      default:     return { color: COLORS.textMuted };
    }
  };
  return (
    <div
      data-testid={`pipeline-stage-${stage.id}`}
      data-failed={isFailed ? "true" : undefined}
      style={{
        background: COLORS.surface, borderRadius: 4,
        border: `1px solid ${COLORS.border}`,
        borderLeft: `3px solid ${accentBar}`,
        padding: "6px 9px",
        marginLeft: depth * 16,
      }}
    >
      {/* Header row — always visible */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
        }}
      >
        <span style={{
          fontSize: 9, fontWeight: 700, color: COLORS.textMuted,
          textTransform: "uppercase", letterSpacing: 0.4, minWidth: 22,
        }}>
          {stage.stepNumber || ""}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: titleColor }}>
          {stage.title}
        </span>
        {pill && (
          <span
            data-testid={`stage-pill-${stage.id}`}
            style={{
              fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8,
              background: pill.bg, color: pill.fg, letterSpacing: 0.3,
            }}
          >
            {pill.label}
          </span>
        )}
        {/* C2 · loop pill on LLM stage — accent-soft / accent-text per mockup */}
        {loop && (
          <span
            data-testid="llm-loop-pill"
            style={{
              fontSize: 9, fontWeight: 700, padding: "1px 8px", borderRadius: 999,
              background: COLORS.accentSoft || "rgba(124,58,237,0.18)",
              color: COLORS.accentText || "#a78bfa", letterSpacing: 0.2,
              whiteSpace: "nowrap",
            }}
          >
            ↻ {loop.iterations} iteration{loop.iterations === 1 ? "" : "s"}
            {loop.reprompts > 0 ? " · re-prompted by fact_check" : ""}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: "monospace",
                       minWidth: 50, textAlign: "right" }}>
          {stage.durationMs !== undefined ? formatDur(stage.durationMs) : "—"}
        </span>
        {delta && (
          <span
            data-testid={`stage-delta-${stage.id}`}
            style={{
              fontSize: 10, color: deltaColor, fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            · {delta.text}
          </span>
        )}
        <span style={{ fontSize: 10, color: COLORS.textMuted, minWidth: 14 }}>
          {open ? "▴" : "▾"}
        </span>
      </div>
      {/* Narrative line — B1 */}
      {narrativeParts && narrativeParts.length > 0 && (
        <div
          data-testid={`stage-narrative-${stage.id}`}
          style={{
            fontSize: 11, color: COLORS.textMuted, lineHeight: 1.45,
            paddingLeft: 30, marginTop: 3,
          }}
        >
          {narrativeParts.map((p, i) => (
            <span key={i} style={partStyle(p.style)}>{p.text}</span>
          ))}
        </div>
      )}
      {/* C1 · parallel branches inside Context Loading — 4-col grid,
          slowest branch highlighted as critical path. Skipped when
          there's only one branch (C1.7 — no value). */}
      {branches && branches.length >= 2 && (
        <div
          style={{
            display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
            gap: 6, marginTop: 6, marginLeft: 30, marginRight: 4,
          }}
        >
          {branches.map(b => (
            <div
              key={b.id}
              data-testid="parallel-branch"
              data-critical={b.critical ? "true" : undefined}
              style={{
                background: b.critical ? "rgba(245,158,11,0.08)" : COLORS.surfaceMuted || "rgba(255,255,255,0.02)",
                border: `1px solid ${b.critical ? "#d97706" : COLORS.border}`,
                borderRadius: 4, padding: "5px 7px",
              }}
            >
              <div style={{
                fontSize: 10, fontWeight: 600,
                color: b.critical ? "#d97706" : COLORS.text,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {b.critical ? "⚡ " : `${b.emoji} `}{b.label}
              </div>
              <div style={{
                fontSize: 9, color: COLORS.textMuted, fontFamily: "monospace",
                marginTop: 1,
              }}>
                {formatDur(b.durationMs)}{b.critical ? " · critical path" : ""}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* E2 + E3 · per-expert micro-pipeline cards (supervisor L3 fan-out).
          Render OUTSIDE the open-block: experts are the primary content of
          the L3 stage, not behind the chevron. 3-col grid on desktop. */}
      {stage.experts && stage.experts.length > 0 && (
        <div
          data-testid="experts-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 6, marginTop: 6, marginLeft: 30, marginRight: 4,
          }}
        >
          {stage.experts.map((exp, i) => (
            <ExpertCard
              key={exp.id || i}
              expert={exp}
              turn={turn}
              baselines={baselines}
              onSpanClick={onSpanClick}
              selectedSpanId={selectedSpanId}
              onChipClick={onChipClick}
            />
          ))}
        </div>
      )}
      {/* E5 · L1.5 narrative folds in under L1 stage when present */}
      {stage._l15Summary && (stage._l15Summary.promoted || stage._l15Summary.cosine) && (
        <div
          data-testid="l15-narrative"
          style={{
            fontSize: 10, color: COLORS.textMuted, fontStyle: "italic",
            paddingLeft: 30, marginTop: 2,
          }}
        >
          L1.5 (BYOK semantic): promoted{" "}
          <span style={{ color: COLORS.text, fontWeight: 600, fontStyle: "normal" }}>
            {stage._l15Summary.promoted || "—"}
          </span>
          {stage._l15Summary.cosine && (
            <> · cosine{" "}
              <span style={{ fontFamily: "monospace", color: COLORS.text, fontStyle: "normal" }}>
                {stage._l15Summary.cosine}
              </span>
            </>
          )}
        </div>
      )}
      {/* C3 · per-stage action chips. Chips with no available data render
          in muted-disabled state with a tooltip explanation (C3.9). */}
      {(() => {
        const chips = getStageChips(stage, turn);
        if (chips.length === 0) return null;
        return (
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 5,
            marginTop: 5, marginLeft: 30,
          }}>
            {chips.map(c => (
              <button
                key={c.kind}
                data-testid={`chip-${stage.id}-${c.kind}`}
                disabled={c.disabled}
                title={c.tooltip || ""}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!c.disabled && onChipClick) onChipClick(c.kind, { stage, turn });
                }}
                style={{
                  background: c.disabled ? "transparent" : "rgba(255,255,255,0.04)",
                  border: `1px solid ${c.disabled ? COLORS.border : (COLORS.accent || "#7c3aed")}`,
                  borderRadius: 999,
                  padding: "2px 9px",
                  fontSize: 10,
                  color: c.disabled ? COLORS.textMuted : (COLORS.accentText || COLORS.accent || "#a78bfa"),
                  cursor: c.disabled ? "not-allowed" : "pointer",
                  opacity: c.disabled ? 0.55 : 1,
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                }}
              >
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
        );
      })()}
      {/* Expanded body — summary fields + click-to-inspect span list */}
      {open && (
        <div style={{ marginTop: 6, paddingLeft: 30 }}>
          {/* Summary KVs */}
          {stage.summary && Object.keys(stage.summary).length > 0 && (
            <div style={{ marginBottom: 6 }}>
              {Object.entries(stage.summary).map(([k, v]) => (
                <div key={k} style={{
                  display: "grid", gridTemplateColumns: "150px 1fr",
                  gap: 6, fontSize: 11, marginTop: 2,
                }}>
                  <span style={{ color: COLORS.textMuted, fontFamily: "monospace" }}>{k}</span>
                  <span style={{ color: COLORS.text, wordBreak: "break-word" }}>
                    {typeof v === "boolean"
                      ? <span style={{ color: v ? "#10b981" : "#ef4444",
                                        fontFamily: "monospace" }}>{String(v)}</span>
                      : String(v)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Experts moved outside the open-block — see below */}
          {/* C5 · drill-down — raw spans of THIS stage with sort + filter
              + export. Box-drawing tree (├─ └─) shows parent_id nesting.
              Critical path = max-duration span (annotated). */}
          {stage.spans && stage.spans.length > 0 && (
            <StageDrillDown
              stage={stage}
              selectedSpanId={selectedSpanId}
              onSpanClick={onSpanClick}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Phase C · C5 StageDrillDown ──────────────────────────────────────
//
// Per-stage drill-down for engineers. Shows the OTel span tree of THIS
// stage with box-drawing characters (├─ └─), critical-path annotation,
// and chips for: sort by duration / sort by start time / GENERATION-only
// filter / export to JSON. Per-stage state — collapsing one stage's
// drill doesn't collapse another (C5.7).

function _treeStringForExport(rows) {
  return rows.map(r => `${"  ".repeat(r.depth)}${r.name}  [${r.type || "SPAN"}] ${r.duration_ms ?? "—"}ms`).join("\n");
}

function StageDrillDown({ stage, onSpanClick, selectedSpanId }) {
  const [sortMode, setSortMode] = React.useState("tree");  // tree | duration | start
  const [genOnly, setGenOnly] = React.useState(false);
  const spans = stage.spans || [];
  const rows = React.useMemo(() => {
    // Build tree rows by parent_id with depth, respecting current sort
    const byId = new Map();
    for (const s of spans) byId.set(s.id, s);
    const children = new Map();
    const roots = [];
    for (const s of spans) {
      if (s.parent_id && byId.has(s.parent_id)) {
        if (!children.has(s.parent_id)) children.set(s.parent_id, []);
        children.get(s.parent_id).push(s);
      } else {
        roots.push(s);
      }
    }
    const sortFn = sortMode === "duration"
      ? (a, b) => (b.duration_ms || 0) - (a.duration_ms || 0)
      : sortMode === "start"
        ? (a, b) => (a.started_at || 0).toString().localeCompare((b.started_at || 0).toString())
        : null;
    if (sortFn) for (const arr of children.values()) arr.sort(sortFn);
    if (sortFn) roots.sort(sortFn);
    const flat = [];
    const visit = (s, depth, isLastSibling, prefix) => {
      flat.push({
        ...s,
        depth,
        prefix: depth > 0 ? `${prefix}${isLastSibling ? "└─ " : "├─ "}` : "",
      });
      const kids = children.get(s.id) || [];
      const childPrefix = depth > 0 ? `${prefix}${isLastSibling ? "   " : "│  "}` : "";
      kids.forEach((c, i) => visit(c, depth + 1, i === kids.length - 1, childPrefix));
    };
    if (sortMode === "tree") {
      roots.forEach((r, i) => visit(r, 0, i === roots.length - 1, ""));
    } else {
      // Flat ordering by chosen sort; ignore tree nesting
      [...spans].sort(sortFn).forEach(s => flat.push({ ...s, depth: 0, prefix: "" }));
    }
    return genOnly
      ? flat.filter(r => r.type === "GENERATION" || /ChatCompletion|TenantLLM|Generation/i.test(r.name))
      : flat;
  }, [spans, sortMode, genOnly]);

  const maxDur = React.useMemo(
    () => spans.reduce((m, s) => Math.max(m, s.duration_ms || 0), 0),
    [spans]
  );

  const onExport = React.useCallback(() => {
    const payload = {
      stage_id: stage.id,
      stage_title: stage.title,
      duration_ms: stage.durationMs,
      span_count: spans.length,
      spans,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stage-${stage.id}-spans.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [stage, spans]);

  if (spans.length === 0) return null;

  const chipBare = (active) => ({
    background: active ? "rgba(255,255,255,0.06)" : "transparent",
    border: `1px solid ${active ? (COLORS.accent || "#7c3aed") : COLORS.border}`,
    color: active ? (COLORS.accentText || COLORS.accent || "#a78bfa") : COLORS.textMuted,
    borderRadius: 999, padding: "1px 8px", fontSize: 10, cursor: "pointer",
    whiteSpace: "nowrap",
  });

  return (
    <div data-testid={`stage-drill-${stage.id}`} style={{ marginTop: 4 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted,
                    textTransform: "uppercase", marginBottom: 3 }}>
        Spans ({rows.length}{genOnly && spans.length !== rows.length ? ` of ${spans.length}` : ""})
      </div>
      {/* C5.5 · sort + filter + export chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
        <button onClick={() => setSortMode("tree")} style={chipBare(sortMode === "tree")}>Tree</button>
        <button onClick={() => setSortMode("duration")} style={chipBare(sortMode === "duration")}>Sort by duration</button>
        <button onClick={() => setSortMode("start")} style={chipBare(sortMode === "start")}>Sort by start time</button>
        <button onClick={() => setGenOnly(!genOnly)} style={chipBare(genOnly)}>Filter: GENERATION only</button>
        <button onClick={onExport} style={chipBare(false)}>Export as JSON</button>
      </div>
      <pre style={{
        background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4,
        padding: 8, margin: 0, fontSize: 10.5, lineHeight: 1.5, overflow: "auto",
        maxHeight: 360,
      }}>
        {rows.length === 0 ? (
          <div style={{ color: COLORS.textMuted, fontStyle: "italic" }}>(no spans match the filter)</div>
        ) : rows.map(r => {
          const isSel = r.id === selectedSpanId;
          const isCritical = (r.duration_ms || 0) === maxDur && maxDur > 0 && rows.length > 1;
          const color = SOURCE_TAG_COLORS[r.source_tag] || SOURCE_TAG_COLORS.manual;
          return (
            <div
              key={r.id}
              onClick={() => onSpanClick && onSpanClick(r)}
              style={{
                display: "flex", alignItems: "baseline", gap: 6,
                padding: "1px 4px", cursor: "pointer",
                background: isSel ? (COLORS.accentSoft || "rgba(124,58,237,0.10)") : "transparent",
                borderRadius: 2,
              }}
            >
              <span style={{ color: COLORS.textMuted, whiteSpace: "pre" }}>{r.prefix}</span>
              <span style={{
                width: 6, height: 6, borderRadius: 1, background: color.fg,
                display: "inline-block", flexShrink: 0,
              }} />
              <span style={{
                color: isCritical ? "#d97706" : COLORS.text,
                fontWeight: isCritical ? 600 : 400,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {r.name}
              </span>
              <span style={{ color: COLORS.textDim || COLORS.textMuted, fontSize: 9 }}>
                {r.type || "SPAN"}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ color: COLORS.textMuted, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                {r.duration_ms !== null && r.duration_ms !== undefined ? `${r.duration_ms}ms` : "—"}
                {isCritical && <span style={{ color: "#d97706", marginLeft: 6 }}>← critical path</span>}
              </span>
            </div>
          );
        })}
      </pre>
    </div>
  );
}

// Expert sub-pipeline (supervisor L3 fan-out). Renders the expert's
// mini chat-pipeline as nested stages inside its own card.
// Phase E · ExpertCard — per-expert micro-pipeline card (E2) + recursive
// drill-in (E3). 6 colored segments map to the same Phase A stages
// (Routing/Input/Context/LLM/Tools/Output); flex-sized proportional to
// each stage's duration so slow segments are visibly wider (E2.7).
// Failed segments turn red and double-flex (E2.8). Click to drill in
// inline (E3.1/E3.5).
function ExpertCard({ expert, onSpanClick, selectedSpanId, baselines, turn, onChipClick }) {
  const [open, setOpen] = React.useState(false);
  const subStages = expert.subStages || [];
  // Pre-compute per-segment durations + status. Always show all 6 segments
  // (matches mockup), missing stages render at minimum flex.
  const segOrder = ["routing", "input_safety", "context", "llm", "tools", "output_safety"];
  const segments = React.useMemo(() => {
    const byId = new Map(subStages.map(s => [s.id, s]));
    return segOrder.map(id => {
      const s = byId.get(id);
      const dur = s?.durationMs || 0;
      const failed = s && (s.spans || []).some(sp => sp.status === "ERROR");
      const verdict = id === "output_safety" ? (turn?.verdict || "").toUpperCase() : null;
      const isFailedSafety = id === "output_safety" && verdict === "INACCURATE";
      return {
        id,
        durationMs: dur,
        failed: failed || isFailedSafety,
        color: STAGE_COLOR[id] || "#7c3aed",
        absent: !s || (s.spans || []).length === 0,
      };
    });
  }, [subStages, turn?.verdict]);
  const totalDur = segments.reduce((a, s) => a + (s.durationMs || 0), 0) || 1;
  const expertPill = expert.status === "dropped"
    ? { label: "FAILED", bg: "rgba(220,38,38,0.18)", fg: "#dc2626" }
    : expert.status === "warn"
      ? { label: "SLOW", bg: "rgba(245,158,11,0.18)", fg: "#d97706" }
      : { label: "OK", bg: "rgba(16,185,129,0.18)", fg: "#059669" };
  const summary = expert._summary || {};
  // E2.9 summary text
  const summaryBits = [];
  if (summary.chunks) summaryBits.push(`${summary.chunks} RAG chunks`);
  if (summary.model) summaryBits.push(String(summary.model));
  if (summary.tools) summaryBits.push(`${summary.tools} tool${summary.tools === 1 ? "" : "s"}`);
  if (summary.verdict) summaryBits.push(`verdict ${summary.verdict}`);
  if (!summaryBits.length) summaryBits.push(`${expert.spans.length} span${expert.spans.length === 1 ? "" : "s"}`);
  return (
    <div
      data-testid={`expert-card-${expert.id}`}
      data-status={expert.status}
      style={{
        background: COLORS.surfaceMuted || COLORS.surface,
        border: `1px solid ${expert.status === "dropped" ? "rgba(220,38,38,0.4)" : COLORS.border}`,
        borderLeft: `3px solid ${expert.status === "dropped" ? "#dc2626" : (COLORS.accent || "#7c3aed")}`,
        borderRadius: 4, padding: "8px 10px", minWidth: 0,
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
      >
        <span style={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>{expert.icon || "🧑‍💼"}</span>
        <span style={{
          fontSize: 12, fontWeight: 700,
          color: expert.status === "dropped" ? "#dc2626" : COLORS.text,
          flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {expert.name || "expert"}
        </span>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8,
          background: expertPill.bg, color: expertPill.fg, letterSpacing: 0.3,
        }}>
          {expertPill.label}
        </span>
        <span style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: "monospace" }}>
          {expert.durationMs !== undefined ? formatDur(expert.durationMs) : "—"}
        </span>
        <span style={{ fontSize: 10, color: COLORS.textMuted, minWidth: 14 }}>
          {open ? "▴" : "▾"}
        </span>
      </div>
      {/* E2.5 · 6-segment mini-pipeline. Each segment flex-grown by its
          stage's duration (E2.7). Failed segments double-flex + red (E2.8). */}
      <div style={{
        display: "flex", gap: 1, marginTop: 5, marginBottom: 5,
        borderRadius: 2, overflow: "hidden",
      }}>
        {segments.map(seg => {
          const proportion = seg.failed
            ? 2  // E2.8 — failed segments flex-grow to draw attention
            : Math.max(0.3, (seg.durationMs / totalDur) * 5);
          return (
            <div
              key={seg.id}
              data-testid={`expert-seg-${seg.id}`}
              data-failed={seg.failed ? "true" : undefined}
              title={`${seg.id}${seg.absent ? " · did not run" : ` · ${formatDur(seg.durationMs)}${seg.failed ? " · FAILED" : ""}`}`}
              style={{
                background: seg.failed ? "#dc2626" : (seg.absent ? "rgba(255,255,255,0.04)" : seg.color),
                height: 8, flex: proportion,
                opacity: seg.absent ? 0.3 : 1,
              }}
            />
          );
        })}
      </div>
      {/* E2.9 · summary line */}
      <div
        data-testid={`expert-summary-${expert.id}`}
        style={{
          fontSize: 10, color: expert.status === "dropped" ? "#dc2626" : COLORS.textMuted,
          marginTop: 2, lineHeight: 1.4,
        }}>
        {summaryBits.join(" · ")}
      </div>
      {/* E3 · drill-in: recursive Phase A+B+C pipeline for this expert. */}
      {open && subStages.length > 0 && (
        <div
          data-testid={`expert-drill-${expert.id}`}
          style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}
        >
          {subStages.map((s, i) => (
            <StageCard
              key={s.id || i}
              stage={s}
              turn={turn}
              baselines={baselines}
              onSpanClick={onSpanClick}
              selectedSpanId={selectedSpanId}
              onChipClick={onChipClick}
              depth={0}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Bucketers ─────────────────────────────────────────────────────
//
// Pure functions: input = list of Span dicts (raw from backend),
// output = ordered list of Stage dicts.
//
// Span name patterns are heuristic — when LangGraph/LlamaIndex adds
// a new node or wrapper, it lands in "Other" until added here.
// Raw timeline toggle catches anything missed.

// Pipeline V2 Phase A · color palette aligned with inspector-pipeline-v2-mockup.html § 1.
// Both input_safety and output_safety use green (they're both "safety" semantically,
// just at different positions in the graph). The split is sequence-correct, not
// visual-discriminating — the position in the pipeline conveys which is which.
const STAGE_COLOR = {
  routing: "#3b82f6",         // blue
  input_safety: "#10b981",    // green
  context: "#14b8a6",         // teal
  llm: "#7c3aed",             // accent / purple
  tools: "#f59e0b",           // amber
  output_safety: "#10b981",   // green
  // Legacy keys retained for back-compat with the supervisor renderer
  // (Phase E will refactor it). bucketSpansForSupervisor still emits `safety`
  // and `output` keys; don't break it before E lands.
  safety: "#10b981",
  output: "#64748b",
  l1: "#3b82f6",
  l15: "#8b5cf6",
  l2: "#7c3aed",
  l3_fanout: "#f59e0b",
  l3_synthesis: "#ec4899",
  other: "#64748b",
};

// Pipeline V2 Phase A · graph-correct bucketing per
// docs/design/inspector-pipeline-v2-phase-A-acceptance.md
//
// Six buckets matching the actual LangGraph node order (see backend
// graph_builder.py): route_agent → check_input_guardrails →
// parallel_prepare/process_attachments/detect_language → call_llm →
// execute_tools → fact_check/check_output_guardrails/check_escalation.
//
// Key changes from the prior 4-bucket model:
//   1. `check_input_guardrails` lives in its OWN bucket (input_safety)
//      instead of being merged with route_agent or output safety.
//   2. `output_safety` is renamed (was `safety`) and renders at the END,
//      with fact_check / output_guardrails / escalation / no_agent_fallback.
//   3. Display order matches actual graph execution order (1-6).
//   4. Parent-walk (A3) shrinks the "Other" bucket dramatically: any span
//      whose parent (or ancestor) is a known LangGraph node bubbles up
//      to that node's bucket. Pre-fix the "Other" bucket held 91% of
//      spans on the Legal Contracts test conv; post-fix it should hold
//      < 10%.
const ROUTING_NODES       = ["route_agent"];
const INPUT_SAFETY_NODES  = ["check_input_guardrails"];
const CONTEXT_NODES       = ["build_context", "parallel_prepare", "process_attachments", "detect_language", "plan_execution"];
const LLM_NODES           = ["call_llm", "aggregate_results"];
const TOOL_NODES          = ["execute_tools", "orchestrate_workflow", "merge_task_results"];
const OUTPUT_SAFETY_NODES = ["fact_check", "check_output_guardrails", "check_escalation", "no_agent_fallback"];

const NODE_TO_BUCKET = new Map();
ROUTING_NODES.forEach(n => NODE_TO_BUCKET.set(n, "routing"));
INPUT_SAFETY_NODES.forEach(n => NODE_TO_BUCKET.set(n, "input_safety"));
CONTEXT_NODES.forEach(n => NODE_TO_BUCKET.set(n, "context"));
LLM_NODES.forEach(n => NODE_TO_BUCKET.set(n, "llm"));
TOOL_NODES.forEach(n => NODE_TO_BUCKET.set(n, "tools"));
OUTPUT_SAFETY_NODES.forEach(n => NODE_TO_BUCKET.set(n, "output_safety"));

// Heuristic name-pattern matcher for instrumented-library spans (openai,
// llama-index, asyncpg, httpx, ES) that don't carry a current_node attr.
// A3 parent-walk handles cases where these spans are children of known
// LangGraph nodes; the heuristics here are the secondary fallback when
// the parent chain doesn't reach a known node (rare in practice).
// `bucketByName` now lives in ./inspectorBucket (D4 — extracted so the
// bucketing logic has a real unit test); imported at the top of this file.

// A3 · parent-walk. Walk a span's ancestor chain via parent_id; return
// the bucket of the first ancestor whose inferred node is a known
// LangGraph node. This is what shrinks "Other" from 91% to <10% — the
// asyncpg BEGIN/COMMIT/SELECT spans, openai ChatCompletion spans, httpx
// POST spans all have parents that ARE LangGraph nodes; before this
// they all defaulted to "Other".
function findAncestorBucket(span, byId, maxDepth = 12) {
  let current = span;
  let depth = 0;
  const seen = new Set();
  while (current && depth < maxDepth) {
    if (seen.has(current.id)) break;  // cycle guard
    seen.add(current.id);
    const inferred = inferLangGraphNodeName(current);
    if (inferred && NODE_TO_BUCKET.has(inferred)) {
      return NODE_TO_BUCKET.get(inferred);
    }
    if (!current.parent_id) break;
    current = byId.get(current.parent_id);
    depth++;
  }
  return null;
}

function bucketSpansForChat(spans) {
  const buckets = {
    routing: [], input_safety: [], context: [], llm: [], tools: [],
    output_safety: [], other: [],
  };

  // Build id-lookup once for parent-walk
  const byId = new Map();
  for (const s of spans || []) byId.set(s.id, s);

  for (const s of spans || []) {
    // 1. Direct LangGraph node match
    const inferred = inferLangGraphNodeName(s);
    if (inferred && NODE_TO_BUCKET.has(inferred)) {
      buckets[NODE_TO_BUCKET.get(inferred)].push(s);
      continue;
    }
    // 2. Heuristic name match (openai, llama-index, ES, httpx)
    const heuristic = bucketByName(s.name);
    if (heuristic) {
      buckets[heuristic].push(s);
      continue;
    }
    // 3. A3 parent-walk: asyncpg BEGIN/COMMIT, openai child spans, etc.
    const ancestor = findAncestorBucket(s, byId);
    if (ancestor) {
      buckets[ancestor].push(s);
      continue;
    }
    // 4. Genuinely unbucketable
    buckets.other.push(s);
  }

  // Graph-execution order (A1.2): 1 Routing → 2 Input Guardrails →
  // 3 Context Loading → 4 LLM Reasoning → 5 Tool Execution →
  // 6 Output Safety. Empty stages render absent via the filter below.
  const stages = [
    makeStage("routing", "1", "Routing", buckets.routing, {
      decision: pickAttr(buckets.routing, ["agent_name", "agent_id", "intent"]),
    }),
    makeStage("input_safety", "2", "Input Guardrails", buckets.input_safety, {
      passed: pickAttr(buckets.input_safety, ["guardrails_passed", "passed"]),
    }),
    makeStage("context", "3", "Context Loading", buckets.context, {
      history: pickAttr(buckets.context, ["history_count"]),
      retrieved_chunks: pickAttr(buckets.context, ["retrieved_chunks_count"]),
      memories: pickAttr(buckets.context, ["memories_count"]),
    }),
    makeStage("llm", "4", "LLM Reasoning", buckets.llm, {
      model: pickModel(buckets.llm),
      llm_calls: buckets.llm.filter(s => s.type === "GENERATION" || /ChatCompletion|TenantLLM/.test(s.name)).length || undefined,
      tokens: pickTokens(buckets.llm),
    }),
    makeStage("tools", "5", "Tool Execution", buckets.tools, {
      tool_calls: pickAttr(buckets.tools, ["tool_call_count"]) || buckets.tools.filter(s => /^POST$/.test(s.name)).length || undefined,
      tools_used: pickToolNames(buckets.tools),
    }),
    makeStage("output_safety", "6", "Output Safety", buckets.output_safety, {
      fact_check: pickAttr(buckets.output_safety, ["verdict", "verification_result"]),
      escalation: pickAttr(buckets.output_safety, ["needs_escalation"]),
      guardrails: pickAttr(buckets.output_safety, ["guardrails_passed"]),
    }),
  ];
  if (buckets.other.length > 0) {
    stages.push(makeStage("other", "·", "Other", buckets.other, {}));
  }
  // Phase A · A1.3 AMENDED (2026-06-24, user feedback): keep all 6 named
  // graph stages visible even when empty so the 6-step structure is
  // perceptible at a glance. StageCard handles the empty-state render.
  // "Other" only renders when non-empty — it's the noise-catcher, not
  // a graph node.
  return stages.filter(s => s.id === "other"
    ? (s.spans.length > 0 || s.experts?.length > 0)
    : true);
}

// ─── Phase B · client-side per-conversation baselines ─────────────────
//
// Per inventory B4 (amended 2026-06-24): aggregate per-stage durations
// across all turns of the CURRENT conversation. No backend endpoint;
// no extra fetch. Memoized in the page on payload.turns.
//
// Returns: { stage_key: { p50_ms, p95_ms, n }, ... } when conv has ≥ 3
// turns; returns null otherwise (too few samples to be meaningful — B4.3
// suppresses delta rendering when this is null).
function computeConversationBaselines(turns) {
  if (!Array.isArray(turns) || turns.length < 3) return null;
  // Stage IDs to sample (matches Phase A bucket keys)
  const STAGES = ["routing", "input_safety", "context", "llm", "tools", "output_safety"];
  const samplesByStage = {};
  for (const sk of STAGES) samplesByStage[sk] = [];
  for (const turn of turns) {
    const spans = turn?.spans || [];
    if (!spans.length) continue;
    const stages = bucketSpansForChat(spans);
    for (const stage of stages) {
      if (stage.durationMs === undefined || stage.durationMs === null) continue;
      if (!samplesByStage[stage.id]) continue;  // skip "other"
      samplesByStage[stage.id].push(stage.durationMs);
    }
  }
  const result = {};
  for (const sk of STAGES) {
    const samples = samplesByStage[sk].sort((a, b) => a - b);
    if (samples.length < 3) continue;  // suppress per-stage when its bucket is sparse
    const p50 = samples[Math.floor(samples.length * 0.5)];
    const p95 = samples[Math.min(samples.length - 1, Math.floor(samples.length * 0.95))];
    result[sk] = { p50_ms: p50, p95_ms: p95, n: samples.length };
  }
  return Object.keys(result).length > 0 ? result : null;
}

// ─── Phase B · per-stage narrative + pill + delta ────────────────────
//
// Stage-aware story line. Inventory B1 (data sources are span attrs +
// summary KVs already extracted by makeStage). Returns an array of
// "parts" — render-time helper turns them into spans w/ optional
// style hints (.delta-up / .delta-bad / .delta-good).
//
// Falls back gracefully when attributes are missing (B6.2):
//   "<duration>s · <span_count> spans"
//
// All numbers cite the per-conversation baseline (B4.4): the caller
// phrases deltas as "this conversation's p50", not "this agent's p50".

function _fmtSec(ms) {
  if (ms === undefined || ms === null) return "—";
  const s = ms / 1000;
  return s >= 10 ? `${s.toFixed(1)}s` : `${s.toFixed(2)}s`;
}

function _deltaFor(durationMs, baseline) {
  if (!baseline || durationMs === undefined || durationMs === null) return null;
  if (durationMs > baseline.p95_ms) {
    return { tier: "bad", text: `above p95 (${_fmtSec(baseline.p95_ms)})` };
  }
  if (durationMs > baseline.p50_ms * 2) {
    const x = (durationMs / baseline.p50_ms).toFixed(1);
    return { tier: "up", text: `${x}× this conv's p50` };
  }
  if (baseline.p50_ms > 0 && durationMs < baseline.p50_ms * 0.7) {
    const x = (baseline.p50_ms / Math.max(durationMs, 1)).toFixed(1);
    return { tier: "good", text: `${x}× faster than usual` };
  }
  return null;
}

function _pickNumber(spans, keys) {
  for (const s of spans) {
    for (const k of keys) {
      const a = s.attributes || {};
      const v = a[k];
      if (typeof v === "number") return v;
      if (typeof v === "string" && !Number.isNaN(Number(v))) return Number(v);
      const meta = a.metadata || {};
      const v2 = meta[k];
      if (typeof v2 === "number") return v2;
      if (typeof v2 === "string" && !Number.isNaN(Number(v2))) return Number(v2);
    }
  }
  return undefined;
}

function computeStageNarrative(stage, turn) {
  if (!stage) return null;
  const spans = stage.spans || [];
  const sum = stage.summary || {};
  const parts = [];
  const push = (text, style) => { if (text) parts.push({ text, style }); };
  switch (stage.id) {
    case "routing": {
      const agent = sum.decision || pickAttr(spans, ["agent_name", "agent_id"]) || "default agent";
      push(`Dispatched `);
      push(String(agent), "bold");
      push(`.`);
      const intent = pickAttr(spans, ["intent", "routing_intent", "matched_intent"]);
      if (intent) {
        push(` Intent: `);
        push(String(intent), "bold");
        push(`.`);
      }
      break;
    }
    case "input_safety": {
      const passed = sum.passed;
      const pii = pickAttr(spans, ["pii_matches", "pii_count"]);
      const inj = pickAttr(spans, ["injection_matches", "injection_count"]);
      if (passed === true || passed === "true" || passed === undefined) {
        push(`PII scan `);
        push(pii ? `${pii} matches` : `clean`, pii ? "up" : "bold");
        push(`. `);
        push(inj && inj !== 0 ? `${inj} injection pattern(s)` : `no prompt-injection patterns`,
             inj && inj !== 0 ? "bad" : "bold");
        push(`.`);
      } else {
        push(`Guardrails `);
        push(`FAILED`, "bad");
        push(`.`);
      }
      break;
    }
    case "context": {
      const hist = sum.history ?? _pickNumber(spans, ["history_count", "history_messages"]);
      const chunks = sum.retrieved_chunks ?? _pickNumber(spans, ["retrieved_chunks_count", "rag_chunk_count"]);
      const mems = sum.memories ?? _pickNumber(spans, ["memories_count", "memory_count"]);
      const tools = _pickNumber(spans, ["mcp_tool_count", "tool_count"]);
      const bits = [];
      if (hist !== undefined) bits.push(`history (${hist} turns)`);
      if (chunks !== undefined) bits.push(`RAG (${chunks} chunks)`);
      if (mems !== undefined) bits.push(`memories (${mems})`);
      if (tools !== undefined) bits.push(`MCP tools (${tools})`);
      if (bits.length > 0) {
        push(`Pulled `);
        push(bits.join(", "), "bold");
        push(`.`);
      } else {
        push(`Loaded context (${spans.length} span${spans.length === 1 ? "" : "s"}).`);
      }
      break;
    }
    case "llm": {
      const model = sum.model || pickModel(spans);
      const calls = sum.llm_calls
        || spans.filter(s => s.type === "GENERATION" || /ChatCompletion|TenantLLM/.test(s.name)).length;
      const tokens = sum.tokens || pickTokens(spans);
      push(`Called `);
      push(model ? String(model) : "LLM", "bold");
      if (calls) {
        push(` `);
        push(`${calls}× `, "bold");
      }
      if (tokens) {
        push(` · `);
        push(`${tokens} tokens`, "bold");
      }
      push(`.`);
      break;
    }
    case "tools": {
      const names = sum.tools_used || pickToolNames(spans);
      const errors = spans.filter(s => s.status === "ERROR").length;
      const total = sum.tool_calls || spans.length;
      if (names) {
        push(`Ran `);
        push(String(names), "bold");
        push(`. `);
        push(`${total - errors}/${total} succeeded`, errors > 0 ? "bad" : "bold");
        push(`.`);
      } else {
        push(`No tool calls.`);
      }
      break;
    }
    case "output_safety": {
      // B6.1 — degrade gracefully when no verification row exists
      if (!turn || (turn.verdict === undefined || turn.verdict === null)
          && (!turn.unsupported_claims || turn.unsupported_claims.length === 0)
          && spans.length === 0) {
        push(`Verification not recorded`, "muted-italic");
        push(`.`);
        break;
      }
      const v = (turn?.verdict || "").toUpperCase();
      push(`fact_check: `);
      if (v === "INACCURATE") push(`INACCURATE`, "bad");
      else if (v === "PARTIAL" || v === "PARTIALLY_ACCURATE") push(`PARTIAL`, "up");
      else if (v === "ACCURATE") push(`ACCURATE`, "good");
      else push(v || "—", "muted-italic");
      push(`. `);
      const reprompt = turn?.re_prompt_count || 0;
      if (reprompt > 0) {
        push(`Re-prompted `);
        push(`${reprompt}×`, "up");
        push(`. `);
      }
      const claims = Array.isArray(turn?.unsupported_claims) ? turn.unsupported_claims : [];
      if (claims.length > 0) {
        push(`Unsupported: "${String(claims[0]).slice(0, 80)}${String(claims[0]).length > 80 ? "…" : ""}"`,
             "bad");
      }
      const guardrails = sum.guardrails;
      if (guardrails === false) {
        push(` guardrails: FAILED`, "bad");
      }
      break;
    }
    // ─── Phase E · Supervisor stage narratives ──────────────────────
    case "l1": {
      // E5 — L1 deterministic + L1.5 semantic combined
      const matched = sum.matched || pickAttr(spans, ["matched_expert", "matched_experts"]);
      const l15Summary = stage._l15Summary || {};
      const promoted = l15Summary.promoted;
      const cosine = l15Summary.cosine;
      if (matched && promoted) {
        push(`Matched: `);
        push(String(matched), "bold");
        push(`. L1.5 added: `);
        push(`${promoted}${cosine ? ` (cosine ${cosine})` : ""}`, "bold");
        push(`.`);
      } else if (matched) {
        push(`Regex matched: `);
        push(String(matched), "bold");
        push(`.`);
      } else if (promoted) {
        push(`L1: no deterministic match. L1.5 promoted: `);
        push(`${promoted}${cosine ? ` (cosine ${cosine})` : ""}`, "bold");
        push(`.`);
      } else {
        push(`No expert dispatched. L2 will answer directly.`, "muted-italic");
      }
      break;
    }
    case "l2": {
      // E4 — per-mode narrative
      const mode = sum.mode || "fan_out";
      const n = sum.experts_dispatched || 0;
      const tokens = sum.tokens;
      if (n === 0) {
        push(`L2 answered directly`, "bold");
        push(` (no expert dispatched).`);
      } else if (mode === "sequential") {
        push(`Chained `);
        push(`${n} expert${n === 1 ? "" : "s"}`, "bold");
        push(`. Each stage feeds the next.`);
      } else if (mode === "conditional") {
        push(`Classified intent → dispatched `);
        push(`${n} expert`, "bold");
        push(` only.`);
      } else if (mode === "review_loop") {
        push(`Delegated → reviewed → accepted`, "bold");
        push(`.`);
      } else {
        push(`Dispatched `);
        push(`${n} expert${n === 1 ? "" : "s"} in parallel`, "bold");
        push(`. Synthesis mode: `);
        push(String(mode), "bold");
        push(`.`);
      }
      if (tokens) {
        push(` · `);
        push(`${tokens} tokens`, "bold");
      }
      break;
    }
    case "l3_fanout": {
      const experts = stage.experts || [];
      const succ = experts.filter(e => e.status === "ok").length;
      const dropped = experts.filter(e => e.status === "dropped").length;
      if (experts.length === 0) {
        push(`No experts dispatched.`, "muted-italic");
      } else {
        push(`${experts.length} expert${experts.length === 1 ? "" : "s"}: `);
        push(experts.map(e => e.name).join(", "), "bold");
        push(`. `);
        push(`${succ}/${experts.length} succeeded`, dropped > 0 ? "bad" : "bold");
        push(`.`);
      }
      break;
    }
    case "l3_synthesis": {
      // E6 — synthesis stage narrative
      const conflicts = Number(sum.conflicts || 0);
      const experts = stage._experts || [];
      const verdict = (turn?.verdict || "").toUpperCase();
      if (experts.length === 0) {
        push(`Nothing to synthesize (no experts ran).`, "muted-italic");
        break;
      }
      const names = experts.filter(e => e.status === "ok").map(e => e.name);
      push(`Merged `);
      push(names.join(" + "), "bold");
      push(` outputs. `);
      if (conflicts > 0) {
        push(`${conflicts} conflict${conflicts === 1 ? "" : "s"} surfaced`, "up");
        push(`. `);
      }
      // Verdict pill (Phase B-style) inline
      if (verdict === "INACCURATE") {
        push(`Verdict: `);
        push(`INACCURATE`, "bad");
      } else if (verdict === "PARTIAL" || verdict === "PARTIALLY_ACCURATE") {
        push(`Verdict: `);
        push(`PARTIAL`, "up");
      } else if (verdict === "ACCURATE") {
        push(`Verdict: `);
        push(`ACCURATE`, "good");
      }
      break;
    }
    default:
      push(`${spans.length} span${spans.length === 1 ? "" : "s"} · ${_fmtSec(stage.durationMs)}`);
  }
  return parts;
}

// ─── Phase C · C1 parallel-branch detection + critical path ──────────
//
// When Context Loading's bucket contains ≥2 child spans of parallel_prepare
// (or its sub-tasks), surface them as a branches sub-block so the operator
// sees which branch was the slowest (critical path). Branches are labeled
// from the span name; the longest one is the critical-path. C1.7: skip
// rendering when there's only one branch — it carries no value.

const _BRANCH_LABELS = [
  { re: /^mcp[._]/i,           emoji: "🔧", label: "MCP discovery" },
  { re: /^rag[._]|rag\.search/i, emoji: "📚", label: "RAG" },
  { re: /^history[._]|history\.load/i, emoji: "📝", label: "History" },
  { re: /^ltm[._]|ltm\.search|memory\.load/i, emoji: "💭", label: "LTM" },
  { re: /intent[._]|intent\.classify/i, emoji: "🎯", label: "Intent" },
  { re: /sentiment/i,          emoji: "🌡", label: "Sentiment" },
  { re: /(detect_language|language)/i, emoji: "🌐", label: "Language" },
  { re: /attach/i,             emoji: "📎", label: "Attachments" },
];

function _labelForBranch(name) {
  const n = (name || "").trim();
  for (const { re, emoji, label } of _BRANCH_LABELS) {
    if (re.test(n)) return { emoji, label };
  }
  return { emoji: "•", label: n || "branch" };
}

function detectParallelBranches(stage) {
  if (!stage || stage.id !== "context") return [];
  const spans = stage.spans || [];
  // Find any span(s) named parallel_prepare or carrying that node attr
  const parents = spans.filter(s => {
    const n = (s.name || "").toLowerCase();
    const node = (s.attributes?.current_node || "").toLowerCase();
    return n === "parallel_prepare" || n.includes("parallel_prepare") || node === "parallel_prepare";
  });
  if (parents.length === 0) return [];
  const parentIds = new Set(parents.map(p => p.id));
  // Direct children of the parallel_prepare span(s)
  const children = spans.filter(s => s.parent_id && parentIds.has(s.parent_id));
  if (children.length < 2) return [];
  const maxDur = children.reduce((m, s) => Math.max(m, s.duration_ms || 0), 0);
  return children.map(s => {
    const { emoji, label } = _labelForBranch(s.name);
    return {
      id: s.id,
      emoji, label,
      durationMs: s.duration_ms || 0,
      critical: (s.duration_ms || 0) === maxDur && maxDur > 0,
      raw: s,
    };
  }).sort((a, b) => b.durationMs - a.durationMs);
}

// ─── Phase C · C2 LLM loop metrics ────────────────────────────────────
//
// On the LLM stage: count GENERATION spans whose parent is call_llm to
// surface the iteration count. Re-prompt is signaled by verification_log
// (re_prompt_count > 0). Single iteration → null (no pill rendered).

function computeLoopMetrics(stage, turn) {
  if (!stage || stage.id !== "llm") return null;
  const spans = stage.spans || [];
  const callLlmIds = new Set(
    spans
      .filter(s => /call_llm/i.test(s.name) || (s.attributes?.current_node || "") === "call_llm")
      .map(s => s.id)
  );
  let iterations = spans.filter(s => {
    if (s.type === "GENERATION") {
      // Only count GENERATIONs whose parent is call_llm (or no parent if
      // there's no nested wrapper — rare).
      if (!s.parent_id) return true;
      return callLlmIds.has(s.parent_id);
    }
    if (/ChatCompletion|TenantLLM/.test(s.name || "")) {
      return !s.parent_id || callLlmIds.has(s.parent_id);
    }
    return false;
  }).length;
  // Floor at 1 so any LLM activity produces an iteration count if the
  // stage is rendered. We still suppress the pill when iterations <= 1.
  if (iterations === 0 && spans.length > 0) iterations = 1;
  const reprompts = Number(turn?.re_prompt_count || 0);
  if (iterations <= 1 && reprompts <= 0) return null;
  return { iterations, reprompts };
}

// ─── Phase C · C4 failure banner ──────────────────────────────────────
//
// When a turn has any FAILED stage (any span ERROR OR INACCURATE verdict),
// build a one-line cause + recovery sentence so the operator sees the
// causal chain at a glance — without scrolling through 100+ spans.

// Delegates to the extracted, unit-tested `firstFailedStage` (D4/F2), passing
// the page's timing-aware `computeStagePill`. "Other" is skipped there.
function _firstFailedStage(stages, turn) {
  return firstFailedStage(stages, turn, computeStagePill);
}

export function computeFailureBanner(stages, turn) {
  if (!turn) return null;
  const hit = _firstFailedStage(stages, turn);
  if (!hit) return null;
  const { stage } = hit;
  const verdict = (turn.verdict || "").toUpperCase();
  const reprompts = Number(turn.re_prompt_count || 0);
  const claims = Array.isArray(turn.unsupported_claims) ? turn.unsupported_claims : [];
  let cause = "An error was raised during execution.";
  let recovery = "See the failed stage for details.";
  if (stage.id === "output_safety" && verdict === "INACCURATE") {
    const claim = claims[0] ? String(claims[0]).slice(0, 120) : "the response";
    cause = `fact_check marked the response INACCURATE: "${claim}"`;
    recovery = reprompts > 0
      ? `Re-prompt loop fired ${reprompts}× and still failed. Reply suppressed; fallback message sent to user.`
      : `Reply suppressed; fallback message sent to user.`;
  } else {
    const errSpan = (stage.spans || []).find(s => s.status === "ERROR");
    if (errSpan) {
      const errClass = errSpan.attributes?.["exception.type"]
                    || errSpan.attributes?.error_class
                    || "error";
      const errMsg = errSpan.attributes?.["exception.message"]
                  || errSpan.attributes?.error_message
                  || "";
      cause = `${errSpan.name} returned ${errClass}${errMsg ? `: "${String(errMsg).slice(0, 80)}"` : ""}.`;
    }
  }
  return {
    stageTitle: stage.title || stage.id,
    pillLabel: hit.pill.label,
    cause,
    recovery,
  };
}

// ─── Phase C · C3 action chips ────────────────────────────────────────
//
// Per-stage chip menu. Chips that need data unavailable in this turn
// render in muted-disabled state with a tooltip explaining why (C3.9).
// Each chip describes its target action by `kind`; the StageCard's
// onChipClick prop routes to a TurnRow-level modal opener.

function getStageChips(stage, turn) {
  if (!stage) return [];
  const spans = stage.spans || [];
  switch (stage.id) {
    case "routing":
      return [];  // C3.1 — none in Phase C
    case "input_safety":
      return [
        { kind: "guardrail_config", emoji: "🔍", label: "See guardrail config",
          disabled: false, tooltip: "Opens the agent's input guardrails config" },
      ];
    case "context": {
      const chunks = Array.isArray(turn?.retrieved_chunks) ? turn.retrieved_chunks : [];
      const branches = detectParallelBranches(stage);
      const mcpBranch = branches.find(b => b.label === "MCP discovery");
      const mcpToolCount = mcpBranch
        ? Number(mcpBranch.raw?.attributes?.mcp_tool_count || mcpBranch.raw?.attributes?.tool_count || 0)
        : 0;
      return [
        { kind: "chunks", emoji: "📚", label: `See retrieved chunks (${chunks.length})`,
          disabled: chunks.length === 0,
          tooltip: chunks.length === 0 ? "No RAG chunks were retrieved on this turn" : "View the RAG chunks the LLM saw" },
        { kind: "mcp_tools", emoji: "🔧", label: `See MCP tools (${mcpToolCount || "—"})`,
          disabled: !mcpBranch,
          tooltip: mcpBranch ? "View the MCP tools loaded for this turn" : "MCP discovery did not run on this turn" },
      ];
    }
    case "llm": {
      const hasReplayable = Boolean(turn?.langfuse_trace_id);
      return [
        { kind: "replay", emoji: "✏️", label: "Replay with edited prompt",
          disabled: !hasReplayable,
          tooltip: hasReplayable ? "Open the replay overlay (Phase 1.5 #1)" : "No Langfuse trace on this turn" },
        { kind: "message_stack", emoji: "📋", label: "See full message stack",
          disabled: false,
          tooltip: "Inspect the messages array sent to the LLM" },
      ];
    }
    case "tools": {
      const toolSpans = spans.filter(s => {
        const n = (s.name || "").toLowerCase();
        return n.startsWith("post") || n.startsWith("get") || /^tool\.|^mcp\.|^agentgateway/.test(n);
      });
      return [
        { kind: "tool_definitions", emoji: "🔧", label: "See tool definitions",
          disabled: toolSpans.length === 0,
          tooltip: toolSpans.length === 0 ? "No tool calls fired on this turn" : "View each tool's JSON schema" },
        { kind: "tool_io", emoji: "📤", label: "See per-tool I/O",
          disabled: toolSpans.length === 0,
          tooltip: toolSpans.length === 0 ? "No tool calls fired on this turn" : "Inspect each tool's input args + output payload" },
      ];
    }
    case "output_safety": {
      const hasVerdict = Boolean(turn?.verdict);
      return [
        { kind: "verdict_accept", emoji: "✓", label: "Accept verdict",
          disabled: !hasVerdict,
          tooltip: hasVerdict ? "Mark this verification as human-reviewed" : "No verification row for this turn" },
        { kind: "verdict_flag", emoji: "✗", label: "Flag as incorrect",
          disabled: !hasVerdict,
          tooltip: hasVerdict ? "Override the verdict and mark reviewed" : "No verification row for this turn" },
        { kind: "verification_row", emoji: "📊", label: "See verification_log row",
          disabled: !hasVerdict,
          tooltip: hasVerdict ? "Open the full verification_log JSON" : "No verification row for this turn" },
      ];
    }
    // ─── Phase E · supervisor stage chips ──────────────────────────
    case "l3_synthesis": {
      // E6.4 + E6.5 — synthesis prompt + per-expert outputs
      const synthesisSpans = stage._synthesisSpans || [];
      const experts = stage._experts || [];
      return [
        { kind: "synthesis_prompt", emoji: "📋", label: "See synthesis prompt",
          disabled: synthesisSpans.length === 0,
          tooltip: synthesisSpans.length === 0 ? "Synthesis didn't run on this turn" : "Open the synthesis LLM call's input" },
        { kind: "expert_outputs", emoji: "🔍", label: `See per-expert outputs (${experts.length})`,
          disabled: experts.length === 0,
          tooltip: experts.length === 0 ? "No expert outputs to compare" : "Open each expert's raw output side-by-side" },
      ];
    }
    default:
      return [];
  }
}

export function computeStagePill(stage, turn, baseline) {
  if (!stage) return null;
  // B2.4 — omit pill when status can't be computed (no spans + no verdict)
  const noSpans = !stage.spans || stage.spans.length === 0;
  // FAILED takes precedence. D1 — for the "Other" catch-all, a telemetry-egress
  // error (OpenObserve/Langfuse/OTEL) is NOT a failure of the turn; a genuine
  // unattributed integration failure still is. `stageHasGenuineError` folds in
  // both that host filter and the output_safety+INACCURATE case.
  if (stageHasGenuineError(stage, turn)) {
    return { label: "FAILED", bg: "rgba(220,38,38,0.18)", fg: "#dc2626" };
  }
  if (noSpans) return null;  // can't compute OK/SLOW without timing
  const delta = _deltaFor(stage.durationMs, baseline?.[stage.id]);
  if (delta && (delta.tier === "bad" || delta.tier === "up")) {
    return { label: "SLOW", bg: "rgba(245,158,11,0.18)", fg: "#d97706" };
  }
  // Only render OK when we actually have a baseline to compare against
  if (baseline?.[stage.id]) return { label: "OK", bg: "rgba(16,185,129,0.18)", fg: "#059669" };
  return null;
}

// ─── Phase D · Sibling-turn diff components ──────────────────────────
//
// D1 DiffToolbar — entry chip "🔀 Compare to another turn", picker
//                  dropdown listing other turns, T1/T2 toggle pills,
//                  "N stages identical · M diverged" counters.
// D2 SiblingDiff — two-column cards (1fr 24px 1fr) with diverged
//                  stages highlighted in red-soft bg + red text.
// D3 DiffNarrative — full-width box with LLM-generated explanation +
//                    "Likely cause:" line in accent-text color.

function _turnLabel(turn, idx) {
  const status = turn?.assistant_message?.id ? "complete" : "in-progress";
  const dur = turn?.duration_ms ? `${(turn.duration_ms / 1000).toFixed(1)}s` : "—";
  const verdict = (turn?.verdict || "").toUpperCase() || "—";
  return `T${idx + 1} · ${status} · ${dur} · ${verdict}`;
}

function DiffToolbar({
  turns, primaryIdx, setPrimaryIdx, compareIdx, openCompare, closeCompare,
  swapPrimary, pickerOpen, setPickerOpen, diffData,
}) {
  if (!turns || turns.length < 2) return null;
  const inDiffMode = compareIdx !== null;
  return (
    <div
      data-testid="diff-toolbar"
      style={{
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 6, padding: "6px 10px", marginBottom: 10,
      }}
    >
      {!inDiffMode ? (
        <>
          <button
            data-testid="chip-diff-open"
            onClick={() => setPickerOpen(!pickerOpen)}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${COLORS.accent || "#7c3aed"}`,
              borderRadius: 999, padding: "3px 12px",
              color: COLORS.accentText || COLORS.accent || "#a78bfa",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}
          >
            🔀 Compare to another turn
          </button>
          {pickerOpen && (
            <div
              data-testid="diff-picker"
              style={{
                position: "absolute", zIndex: 30,
                background: COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6, padding: 6, marginTop: 36,
                boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
                minWidth: 240,
              }}
            >
              {turns.map((t, i) => (
                <button
                  key={i}
                  onClick={() => openCompare(i)}
                  disabled={i === primaryIdx}
                  style={{
                    width: "100%", textAlign: "left",
                    background: i === primaryIdx ? "rgba(255,255,255,0.04)" : "transparent",
                    border: "none", color: i === primaryIdx ? COLORS.textMuted : COLORS.text,
                    padding: "5px 8px", fontSize: 11, cursor: i === primaryIdx ? "not-allowed" : "pointer",
                    borderRadius: 3,
                  }}
                >
                  {_turnLabel(t, i)}{i === primaryIdx ? " · (primary)" : ""}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <span style={{ fontSize: 11, color: COLORS.textMuted }}>Compare:</span>
          <button
            data-testid="diff-toggle-left"
            onClick={() => { /* primaryIdx unchanged — left is already primary */ }}
            style={{
              background: COLORS.accentSoft || "rgba(124,58,237,0.18)",
              border: `1px solid ${COLORS.accent || "#7c3aed"}`,
              borderRadius: 999, padding: "2px 10px",
              color: COLORS.accentText || "#a78bfa",
              fontSize: 11, fontWeight: 600, cursor: "default",
            }}
          >
            T{primaryIdx + 1}
          </button>
          <span style={{ color: COLORS.textDim || COLORS.textMuted, fontSize: 11 }}>vs</span>
          <button
            data-testid="diff-toggle-right"
            onClick={swapPrimary}
            style={{
              background: "transparent",
              border: `1px solid ${COLORS.border}`,
              borderRadius: 999, padding: "2px 10px",
              color: COLORS.text, fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}
            title="Click to swap which side is primary"
          >
            T{compareIdx + 1}
          </button>
          <span style={{ flex: 1 }} />
          {diffData && (
            <span
              data-testid="diff-counts"
              style={{ fontSize: 11, color: COLORS.textMuted }}
            >
              {diffData.identical_count} identical · {diffData.diverged_count} diverged
            </span>
          )}
          <button
            data-testid="diff-close"
            onClick={closeCompare}
            style={{
              background: "transparent", border: "none",
              color: COLORS.textMuted, cursor: "pointer", fontSize: 12, padding: 4,
            }}
          >
            ✕
          </button>
        </>
      )}
    </div>
  );
}

function _DiffStageRow({ delta, side }) {
  const text = side === "left" ? delta.left_summary : delta.right_summary;
  const diverged = delta.diverged;
  return (
    <div
      data-testid={`diff-stage-${delta.stage_id}`}
      data-diverged={diverged ? "true" : undefined}
      style={{
        fontSize: 11, padding: "4px 7px",
        background: diverged ? "rgba(220,38,38,0.10)" : "transparent",
        color: diverged ? "#dc2626" : COLORS.textMuted,
        borderRadius: 3, marginBottom: 2,
      }}
    >
      <span style={{ fontWeight: 600, textTransform: "capitalize" }}>
        {delta.stage_id.replace("_", " ")}
      </span>
      <span style={{ marginLeft: 4 }}>→ {text}</span>
    </div>
  );
}

function SiblingDiff({ leftTurn, rightTurn, leftIdx, rightIdx, diffData, diffLoading, diffError }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 24px 1fr",
        gap: 0, alignItems: "stretch",
      }}>
        <div
          data-testid="diff-card-left"
          style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 6, padding: 8,
          }}
        >
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            paddingBottom: 5, marginBottom: 6,
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.text,
                          textTransform: "uppercase", letterSpacing: 0.4 }}>
              Turn {leftIdx + 1} · {(leftTurn?.verdict || "—")}
            </span>
            <span style={{ fontFamily: "monospace", fontSize: 10, color: COLORS.textMuted }}>
              {leftTurn?.duration_ms ? `${(leftTurn.duration_ms / 1000).toFixed(1)}s` : "—"}
            </span>
          </div>
          {diffData?.stage_deltas?.map(d => (
            <_DiffStageRow key={d.stage_id} delta={d} side="left" />
          )) || <_DiffPlaceholder loading={diffLoading} error={diffError} />}
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          color: COLORS.accent || "#7c3aed", fontSize: 18,
        }}>⇄</div>
        <div
          data-testid="diff-card-right"
          style={{
            background: COLORS.surface,
            border: `1px solid ${diffData && diffData.diverged_count > 0 ? "rgba(220,38,38,0.4)" : COLORS.border}`,
            borderLeft: `3px solid ${diffData && diffData.diverged_count > 0 ? "#dc2626" : COLORS.border}`,
            borderRadius: 6, padding: 8,
          }}
        >
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            paddingBottom: 5, marginBottom: 6,
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <span style={{ fontSize: 10, fontWeight: 700,
                          color: diffData && diffData.diverged_count > 0 ? "#dc2626" : COLORS.text,
                          textTransform: "uppercase", letterSpacing: 0.4 }}>
              Turn {rightIdx + 1} · {(rightTurn?.verdict || "—")}
            </span>
            <span style={{ fontFamily: "monospace", fontSize: 10, color: COLORS.textMuted }}>
              {rightTurn?.duration_ms ? `${(rightTurn.duration_ms / 1000).toFixed(1)}s` : "—"}
            </span>
          </div>
          {diffData?.stage_deltas?.map(d => (
            <_DiffStageRow key={d.stage_id} delta={d} side="right" />
          )) || <_DiffPlaceholder loading={diffLoading} error={diffError} />}
        </div>
      </div>
      {/* D3 · narrative box */}
      <DiffNarrative diffData={diffData} loading={diffLoading} error={diffError} />
    </div>
  );
}

function _DiffPlaceholder({ loading, error }) {
  return (
    <div style={{ fontSize: 10, color: COLORS.textMuted, fontStyle: "italic", padding: 6 }}>
      {error ? `error: ${error}` : loading ? "computing diff…" : "—"}
    </div>
  );
}

function DiffNarrative({ diffData, loading, error }) {
  if (loading && !diffData) {
    return (
      <div
        data-testid="diff-narrative"
        style={{
          marginTop: 8, padding: "8px 12px", fontSize: 11,
          color: COLORS.textMuted, fontStyle: "italic",
          background: "rgba(255,255,255,0.02)",
          border: `1px solid ${COLORS.border}`, borderRadius: 6,
        }}
      >
        Generating diff narrative…
      </div>
    );
  }
  if (error || !diffData) return null;
  const isDiverged = diffData.diverged_count > 0;
  return (
    <div
      data-testid="diff-narrative"
      data-source={diffData.source || "unknown"}
      style={{
        marginTop: 8, padding: "10px 12px",
        background: isDiverged ? "rgba(220,38,38,0.08)" : "rgba(16,185,129,0.06)",
        border: `1px solid ${isDiverged ? "rgba(220,38,38,0.30)" : "rgba(16,185,129,0.25)"}`,
        borderLeft: `3px solid ${isDiverged ? "#dc2626" : "#10b981"}`,
        borderRadius: 6,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700,
                    color: isDiverged ? "#dc2626" : "#059669",
                    textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
        Diff narrative {diffData.source === "rule" && (
          <span style={{ color: COLORS.textMuted, fontSize: 9, fontWeight: 500,
                        textTransform: "none", marginLeft: 6 }}>
            (rule-based · LLM unavailable)
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.55 }}>
        {diffData.narrative}
      </div>
      {diffData.likely_cause && (
        <div style={{ fontSize: 12, color: COLORS.accentText || "#a78bfa",
                      marginTop: 6, fontWeight: 600 }}>
          Likely cause: <span style={{ fontWeight: 400 }}>{diffData.likely_cause}</span>
        </div>
      )}
    </div>
  );
}

// D5 · AnomalyBadge — surfaces above the transcript when (a) the current
// turn has > 2 SLOW/FAILED stages OR (b) the agent has matching failure
// clusters from recent traffic. Tooltip lists which stages / patterns.
function AnomalyBadge({ clusters, turn }) {
  if (!clusters || clusters.length === 0) return null;
  // For v1, render whenever the agent has ANY clusters with recent_count >= 2;
  // D5.3 — when current turn's verdict matches a cluster, mark it strongly.
  const verdict = (turn?.verdict || "").toUpperCase();
  const matching = clusters.filter(c => c.verdict === verdict);
  const isStrong = matching.length > 0;
  const tooltip = clusters.slice(0, 5).map(c =>
    `${c.signature} — ${c.recent_count}× in last 30d`
  ).join("\n");
  return (
    <div
      data-testid="anomaly-badge"
      title={tooltip}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        background: isStrong ? "rgba(220,38,38,0.10)" : "rgba(245,158,11,0.10)",
        border: `1px solid ${isStrong ? "rgba(220,38,38,0.30)" : "rgba(245,158,11,0.30)"}`,
        borderLeft: `3px solid ${isStrong ? "#dc2626" : "#d97706"}`,
        borderRadius: 6, padding: "6px 10px", marginBottom: 10,
        fontSize: 11, color: COLORS.text, cursor: "help",
      }}
    >
      <span style={{ fontSize: 14 }}>{isStrong ? "🚨" : "⚠️"}</span>
      <span style={{ fontWeight: 600 }}>
        {isStrong
          ? `This turn matches a known failure cluster (${matching[0].recent_count}× in last 30d)`
          : `Agent has ${clusters.length} active failure pattern${clusters.length === 1 ? "" : "s"}`}
      </span>
      <span style={{ color: COLORS.textMuted, fontSize: 10 }}>
        — hover for detail
      </span>
    </div>
  );
}

// Phase E · 4-stage Supervisor renderer (replaces the pre-E 6-stage form).
// Stages: L1 (folds in L1.5 narrative), L2, L3-fanout (+experts), L3-synthesis.
// Safety/escalation collapse into L3-synthesis's verdict pill — the v13 design
// puts fact_check / output_safety AFTER synthesis at the supervisor level.
function bucketSpansForSupervisor(spans) {
  const buckets = {
    l1: [], l15: [], l2: [], l3_fanout: [], l3_synthesis: [],
    safety: [], experts: new Map(), other: [],
  };

  // Helper — direct name → bucket key. Returns null if name doesn't match.
  const nameToBucket = (n) => {
    if (/^supervisor\.l1[_.]/.test(n) || n === "supervisor.l1" || /l1_router/.test(n)) return "l1";
    if (/^supervisor\.l15/.test(n) || n === "supervisor.l15" || /l15_semantic|semantic_router/.test(n)) return "l15";
    if (/^supervisor\.l2/.test(n) || n === "supervisor.l2" || /l2_lead/.test(n)) return "l2";
    if (/^supervisor\.l3_(fan|fanout|sequential|conditional|review)/.test(n)) return "l3_fanout";
    if (/^supervisor\.l3_synthes/.test(n) || /l3_synthesis/.test(n)) return "l3_synthesis";
    if (/^langgraph\.(fact_check|check_output_guardrails|check_escalation)/.test(n)) return "safety";
    return null;
  };
  const isSubagent = (n) => /^subagent\./.test(n) || /knowledge_subagent|action_subagent/.test(n);

  // Build id-lookup for parent-walk — same pattern as bucketSpansForChat.
  // Without this, spans like `ChatCompletion` / `POST` / `CreateEmbeddings`
  // whose direct name doesn't match any supervisor bucket fall through to
  // "Other" — even when their PARENT is a supervisor.l2 / subagent.X span.
  // User-reported 2026-06-25: fresh supervisor turn showed "Other 17 spans
  // · 9.27s" because all the auto-instrumentor children of supervisor
  // spans were unbucketed.
  const byId = new Map();
  for (const s of spans || []) byId.set(s.id, s);

  // Walk a span's ancestor chain; return either {bucket: "<key>"} or
  // {subagent: "<expert_name>"} for the first ancestor that matches.
  const findAncestorAttribution = (span, maxDepth = 12) => {
    let cur = span;
    const seen = new Set();
    let depth = 0;
    while (cur && depth < maxDepth) {
      if (seen.has(cur.id)) break;
      seen.add(cur.id);
      const cn = (cur.name || "").trim();
      const b = nameToBucket(cn);
      if (b) return { bucket: b };
      if (isSubagent(cn)) {
        const ename = pickAttrFromSpan(cur, ["expert_name", "subagent_name", "agent_name"])
          || cn.replace(/^subagent\./, "") || "expert";
        return { subagent: ename };
      }
      if (!cur.parent_id) break;
      cur = byId.get(cur.parent_id);
      depth += 1;
    }
    return null;
  };

  for (const s of spans || []) {
    const n = (s.name || "").trim();
    // 1. Direct name match
    const b = nameToBucket(n);
    if (b) { buckets[b].push(s); continue; }
    if (isSubagent(n)) {
      const ename = pickAttrFromSpan(s, ["expert_name", "subagent_name", "agent_name"])
        || n.replace(/^subagent\./, "") || "expert";
      if (!buckets.experts.has(ename)) buckets.experts.set(ename, []);
      buckets.experts.get(ename).push(s);
      continue;
    }
    // 2. Parent-walk — absorb children of supervisor.* / subagent.* spans
    //    into the right bucket / expert. This is what shrinks "Other"
    //    from 17 to ~0 on supervisor turns.
    const ancestor = findAncestorAttribution(s);
    if (ancestor) {
      if (ancestor.bucket) buckets[ancestor.bucket].push(s);
      else if (ancestor.subagent) {
        if (!buckets.experts.has(ancestor.subagent)) buckets.experts.set(ancestor.subagent, []);
        buckets.experts.get(ancestor.subagent).push(s);
      }
      continue;
    }
    // 3. Auto-instrumentor noise — at this point we already failed to
    //    find any semantic ancestor (step 2 returned null), so the span
    //    is NOT attached to a supervisor.l* / subagent.* tree. Either:
    //      (a) the span is a true orphan (parent_id = null)
    //      (b) the span's parent IS another auto-instrumented span
    //          (e.g. POST → ChatCompletion → null), but none in the
    //          chain reach a semantic ancestor
    //    In both cases the operator gets no useful info from rendering
    //    these in the pipeline view — they're LlamaIndex sub-engine
    //    calls / asyncpg internals / httpx outbound retries. They
    //    remain visible via the "▾ raw timeline" toggle.
    //
    //    User-reported 2026-06-25: 17 spans / 9.27s in "Other" on
    //    every supervisor turn because these instrumentor children
    //    didn't propagate the supervisor's OTEL context. Drop them.
    if (/^(ChatCompletion|POST|GET|CreateEmbeddings|search|index|indices\.exists)$/.test(n)) {
      continue;
    }

    // 4. Genuinely unbucketable — drop into Other as a last resort
    buckets.other.push(s);
  }

  // Detect synthesis mode from L2 or L3-fanout span attrs.
  // Default to fan_out when ≥2 experts, single when 1, none when 0.
  const inferredMode = (
    pickAttr(buckets.l3_fanout, ["mode", "synthesis_mode"])
    || pickAttr(buckets.l2, ["synthesis_mode", "mode"])
    || (buckets.experts.size > 1 ? "fan_out" : (buckets.experts.size === 1 ? "conditional" : "direct"))
  );

  // Build expert cards with per-expert sub-pipelines (E2)
  const experts = Array.from(buckets.experts.entries()).map(([name, expertSpans]) => {
    const subStages = bucketSpansForChat(expertSpans);
    const hasError = expertSpans.some(s => s.status === "ERROR");
    const dur = sumDurations(expertSpans);
    // Pick a heuristic icon from the expert name's first non-emoji char
    // (E2.4 — minimal heuristic OK for v1)
    const lower = String(name).toLowerCase();
    let icon = "🧑‍💼";
    if (/network|noc|telecom/.test(lower)) icon = "🌐";
    else if (/code|engineer|dev/.test(lower)) icon = "🧑‍💻";
    else if (/billing|finance|invoice|payment/.test(lower)) icon = "💳";
    else if (/legal|contract/.test(lower)) icon = "⚖️";
    else if (/health|medic|patient/.test(lower)) icon = "🩺";
    else if (/sales|crm/.test(lower)) icon = "💼";
    else if (/it.?help|support|helpdesk/.test(lower)) icon = "🛠";
    return {
      id: pickAttrFromSpan(expertSpans[0] || {}, ["expert_agent_id", "agent_id"]) || name,
      name,
      icon,
      spans: expertSpans,
      subStages,
      durationMs: dur,
      status: hasError ? "dropped" : "ok",
      // E2.9 — summary fact line below the micro-pipeline
      _summary: {
        chunks: pickAttr(expertSpans, ["retrieved_chunks_count", "rag_chunk_count"]),
        model: pickModel(expertSpans),
        tools: pickAttr(expertSpans, ["tool_call_count"]) || expertSpans.filter(s => /^POST$/.test(s.name)).length,
        verdict: pickAttr(expertSpans, ["verdict", "verification_result"]),
      },
    };
  });

  // E1.5 — adapt L3 stage label to synthesis mode
  const l3Labels = {
    fan_out: `L3 · Expert Fan-Out (parallel)`,
    sequential: `L3 · Sequential Chain`,
    conditional: `L3 · Conditional Dispatch`,
    review_loop: `L3 · Review Loop`,
    single: `L3 · Expert Dispatch`,
    direct: `L3 · Expert Dispatch`,
  };

  // Stash L1.5 + safety + mode + experts on the L1/L3 stages so the
  // narrative + render helpers below can fold them in. Carry the raw
  // spans through too so drill-in works.
  const stages = [
    {
      ...makeStage("l1", "1", "L1 · Deterministic Router", buckets.l1, {
        matched: pickAttr(buckets.l1, ["matched_expert", "matched_experts", "rule"]),
      }),
      _l15Spans: buckets.l15,
      _l15Summary: {
        promoted: pickAttr(buckets.l15, ["top_match", "matched_expert", "promoted_expert"]),
        cosine: pickAttr(buckets.l15, ["cosine_score"]),
      },
    },
    makeStage("l2", "2",
      `L2 · Lead Agent${pickModel(buckets.l2) ? ` (${pickModel(buckets.l2)})` : ""}`,
      buckets.l2,
      {
        mode: inferredMode,
        experts_dispatched: experts.length,
        tokens: pickTokens(buckets.l2),
      }),
    {
      id: "l3_fanout",
      stepNumber: "3",
      title: l3Labels[inferredMode] || l3Labels.fan_out,
      spans: buckets.l3_fanout,
      experts,
      summary: {
        mode: inferredMode,
        experts: experts.length,
        succeeded: experts.filter(e => e.status === "ok").length,
        dropped: experts.filter(e => e.status === "dropped").length,
      },
      summaryShort: experts.map(e => e.name).join(", "),
      durationMs: sumDurations(buckets.l3_fanout) + experts.reduce((acc, e) => acc + (e.durationMs || 0), 0),
      status: experts.some(e => e.status === "dropped") ? "warn" : "ok",
      _mode: inferredMode,
    },
    {
      ...makeStage("l3_synthesis", "4", "L3 · Synthesis (citation-strict)",
        [...buckets.l3_synthesis, ...buckets.safety], {
          model: pickModel(buckets.l3_synthesis),
          conflicts: pickAttr(buckets.l3_synthesis, ["conflicts_surfaced", "conflict_count"]),
          re_prompt: pickAttr(buckets.l3_synthesis, ["re_prompt_used"]),
          tokens: pickTokens(buckets.l3_synthesis),
        }),
      _experts: experts,
      _synthesisSpans: buckets.l3_synthesis,
    },
  ];
  if (buckets.other.length > 0) {
    stages.push(makeStage("other", "·", "Other", buckets.other, {}));
  }
  // E1.6 + Phase A A1.3 amendment — keep the 4 supervisor stages always
  // visible (muted skeleton when empty); only "other" hides when empty.
  return stages.filter(s => s.id === "other"
    ? (s.spans.length > 0)
    : true);
}

// ─── Bucketer helpers ──────────────────────────────────────────────

// LangGraph emits langgraph.N (numbered) when serialized.name is empty,
// which our callback can't avoid. We use 3 resolution strategies:
//   1. Span name suffix if non-numeric
//   2. `current_node` attribute (set by some nodes' state delta)
//   3. Key signatures in output_preview (each node returns a characteristic
//      shape — e.g. `reply` + `tool_calls_requested` → call_llm)
function inferLangGraphNodeName(span) {
  if (!span || !(span.name || "").startsWith("langgraph.")) return null;
  // 1) span name has the node already (e.g. langgraph.call_llm)
  const suffix = span.name.replace(/^langgraph\./, "");
  if (suffix && !/^\d+$/.test(suffix) && suffix !== "node") return suffix;
  // 2) explicit current_node in output_preview (some nodes set it)
  const parsedOutput = parseSpanOutput(span);
  if (parsedOutput && parsedOutput.current_node) return parsedOutput.current_node;
  // 3) attributes — Langfuse stores the OTEL bag under s.attributes;
  //    dig into nested {attributes: '{"k":"v"}'} string-encoded form
  const a = span.attributes || {};
  if (a.current_node) return a.current_node;
  const otelAttrs = a.attributes;
  if (typeof otelAttrs === "string") {
    try {
      const parsed = JSON.parse(otelAttrs);
      if (parsed && parsed.current_node) return parsed.current_node;
    } catch { /* ignore */ }
  }
  // 4) infer from output-key signatures — each LangGraph node returns
  //    a characteristic state delta. Order matters: most-specific first.
  if (parsedOutput) {
    const keys = new Set(Object.keys(parsedOutput));
    if (keys.has("tool_results")) return "execute_tools";
    if (keys.has("reply") || keys.has("tool_calls_requested")
        || keys.has("finish_reason") || keys.has("usage")) return "call_llm";
    if (keys.has("needs_escalation") || keys.has("escalation_reasons")
        || keys.has("is_resolved")) return "check_escalation";
    if (keys.has("rich_elements")) return "check_output_guardrails";
    if (keys.has("verdict") || keys.has("verification_result")) return "fact_check";
    if (keys.has("intent") && keys.has("agent_id")) return "route_agent";
    if (keys.has("enhanced_prompt") || keys.has("retrieved_chunks")
        || keys.has("history")) return "build_context";
    if (keys.has("attachment_context")) return "process_attachments";
    if (keys.has("detected_language")) return "detect_language";
  }
  return null;
}

function parseSpanOutput(span) {
  const op = span.output_preview;
  if (!op) return null;
  try {
    return typeof op === "string" ? JSON.parse(op) : op;
  } catch {
    return null;
  }
}

function makeStage(id, stepNumber, title, spans, summary = {}) {
  const cleanSummary = {};
  for (const [k, v] of Object.entries(summary)) {
    if (v !== undefined && v !== null && v !== "" && v !== false) cleanSummary[k] = v;
    else if (v === false && (k === "needs_escalation" || k === "is_resolved")) cleanSummary[k] = v;
  }
  const summaryShort = Object.entries(cleanSummary)
    .slice(0, 2)
    .map(([k, v]) => `${k}: ${String(v).slice(0, 30)}`)
    .join(" · ") || (spans.length > 0 ? `${spans.length} span${spans.length === 1 ? "" : "s"}` : "");
  return {
    id, stepNumber, title, spans: spans || [],
    summary: cleanSummary,
    summaryShort,
    durationMs: sumDurations(spans),
  };
}

function sumDurations(spans) {
  if (!spans || spans.length === 0) return undefined;
  // Use max instead of sum — async spans run concurrently; sum overcounts
  const tops = spans.filter(s => !s.parent_id || !spans.some(p => p.id === s.parent_id));
  if (tops.length === 0) return spans.reduce((a, s) => Math.max(a, s.duration_ms || 0), 0);
  return tops.reduce((a, s) => a + (s.duration_ms || 0), 0);
}

function pickAttr(spans, keys) {
  for (const s of spans) {
    for (const k of keys) {
      const a = s.attributes || {};
      if (a[k] !== undefined && a[k] !== null && a[k] !== "") return a[k];
      const meta = a.metadata || {};
      if (meta[k] !== undefined && meta[k] !== null && meta[k] !== "") return meta[k];
    }
  }
  return undefined;
}

function pickAttrFromSpan(span, keys) {
  const a = span.attributes || {};
  for (const k of keys) {
    if (a[k] !== undefined && a[k] !== null && a[k] !== "") return a[k];
  }
  return undefined;
}

function pickModel(spans) {
  for (const s of spans) {
    if (s.model) return s.model;
    const m = pickAttrFromSpan(s, ["llm.model_name", "gen_ai.request.model", "model"]);
    if (m) return m;
  }
  return undefined;
}

function pickTokens(spans) {
  let pin = 0, pout = 0;
  for (const s of spans) {
    pin += s.prompt_tokens || 0;
    pout += s.completion_tokens || 0;
  }
  if (pin === 0 && pout === 0) return undefined;
  return `${pin} → ${pout}`;
}

function pickToolNames(spans) {
  const names = new Set();
  for (const s of spans) {
    const n = s.name || "";
    // MCP tool POSTs look like `POST /api/...` — name is just "POST"; check attributes
    const toolName = pickAttrFromSpan(s, ["tool_name", "tool.name", "http.url"]);
    if (toolName) names.add(String(toolName).split("/").pop().slice(0, 40));
    else if (n && n !== "POST" && n !== "GET") names.add(n.slice(0, 40));
  }
  if (names.size === 0) return undefined;
  return Array.from(names).slice(0, 4).join(", ");
}


// ─── SpanDetailPanel ──────────────────────────────────────────────

function SpanDetailPanel({ turn }) {
  const { span, turn_index } = turn;
  const color = SOURCE_TAG_COLORS[span.source_tag] || SOURCE_TAG_COLORS.manual;
  return (
    <div style={{ padding: "16px 18px" }}>
      <div style={{ fontSize: 9, color: COLORS.textMuted,
                    textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
        selected span · T{turn_index + 1}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
        <span style={{ width: 10, height: 10, borderRadius: 2, background: color.fg }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{span.name}</div>
      </div>
      <Pill bg={color.bg} fg={color.fg}>{span.source_tag}</Pill>

      <KV label="type" value={span.type} />
      <KV label="duration" value={span.duration_ms ? formatDur(span.duration_ms) : "—"} />
      <KV label="status" value={span.status} />
      {span.model && <KV label="model" value={span.model} />}
      {(span.prompt_tokens || span.completion_tokens) && (
        <KV label="tokens" value={`${span.prompt_tokens || 0} → ${span.completion_tokens || 0}`} />
      )}
      {span.input_preview && (
        <Section title="input"><SmartPayload value={span.input_preview} /></Section>
      )}
      {span.output_preview && (
        <Section title="output"><SmartPayload value={span.output_preview} /></Section>
      )}
      {span.attributes && Object.keys(span.attributes).length > 0 && (
        <Section title="attributes">
          <SmartAttributes attrs={span.attributes} />
        </Section>
      )}
    </div>
  );
}


// ─── Smart payload renderer ───────────────────────────────────────
//
// Spans carry varied shapes: structured JSON (dict/list), plain
// strings (prompts/replies/SQL), or LLM-shaped JSON wrapping markdown
// content. Default behaviour pre-2026-06-23 was raw <pre>JSON</pre>
// for everything → unreadable for LLM prompts + replies with newlines,
// bullets, code blocks. This renderer picks the right view per shape.

function SmartPayload({ value }) {
  // Empty / null
  if (value === null || value === undefined || value === "") return null;

  // Already a string — try parse as JSON first, otherwise render markdown
  if (typeof value === "string") {
    const parsed = tryParseJson(value);
    if (parsed !== undefined) return <JsonPayload data={parsed} />;
    // Plain string — render as Markdown so prompts / replies are readable
    return (
      <div style={{
        background: COLORS.surfaceMuted || COLORS.bg,
        border: `1px solid ${COLORS.border}`, borderRadius: 4,
        padding: 10, fontSize: 12, color: COLORS.text,
        maxHeight: 320, overflow: "auto",
      }}>
        <MarkdownText text={value} />
      </div>
    );
  }

  // Already an object / array → JSON-shaped renderer
  return <JsonPayload data={value} />;
}

function tryParseJson(s) {
  if (typeof s !== "string") return undefined;
  const t = s.trim();
  if (!t) return undefined;
  if (!(t.startsWith("{") || t.startsWith("[") || t.startsWith('"'))) return undefined;
  try { return JSON.parse(t); } catch { return undefined; }
}

// JsonPayload renders an object/list with each TOP-LEVEL key collapsed
// to a label + a smart-rendered value. Long strings inside the JSON
// (prompts, replies, system_prompt_preview) render as Markdown; nested
// objects render as compact JSON; small scalars render inline.
function JsonPayload({ data }) {
  if (data === null || data === undefined) return null;
  if (typeof data !== "object") {
    // Scalar → markdown for string, otherwise code
    return typeof data === "string"
      ? <MarkdownText text={data} />
      : <Code>{String(data)}</Code>;
  }
  if (Array.isArray(data)) {
    // Array of primitives → render inline; array of objects → JSON
    const allScalar = data.every(d => typeof d !== "object" || d === null);
    if (allScalar) return <Code>{JSON.stringify(data, null, 2)}</Code>;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.slice(0, 20).map((item, i) => (
          <div key={i} style={{
            border: `1px solid ${COLORS.border}`, borderRadius: 4,
            padding: 6, background: COLORS.surface,
          }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted,
                          marginBottom: 4, textTransform: "uppercase" }}>
              [{i}]
            </div>
            <JsonPayload data={item} />
          </div>
        ))}
        {data.length > 20 && (
          <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: "italic" }}>
            …{data.length - 20} more items
          </div>
        )}
      </div>
    );
  }
  // Object → key-value rows, each value smart-rendered
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {Object.entries(data).map(([k, v]) => (
        <div key={k}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
                        marginBottom: 2, fontFamily: "monospace" }}>
            {k}
          </div>
          {renderJsonValue(v)}
        </div>
      ))}
    </div>
  );
}

function renderJsonValue(v) {
  if (v === null || v === undefined) {
    return <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: "italic" }}>null</div>;
  }
  if (typeof v === "boolean") {
    return <div style={{ fontSize: 11, color: v ? "#10b981" : "#ef4444",
                         fontFamily: "monospace" }}>{String(v)}</div>;
  }
  if (typeof v === "number") {
    return <div style={{ fontSize: 11, color: COLORS.text, fontFamily: "monospace" }}>{v}</div>;
  }
  if (typeof v === "string") {
    // Long strings or strings with newlines → Markdown render
    const looksLikeProse = v.length > 80 || v.includes("\n");
    if (looksLikeProse) {
      return (
        <div style={{
          background: COLORS.surfaceMuted || COLORS.bg,
          border: `1px solid ${COLORS.border}`, borderRadius: 4,
          padding: 8, fontSize: 12, color: COLORS.text,
          maxHeight: 240, overflow: "auto",
        }}>
          <MarkdownText text={v} />
        </div>
      );
    }
    return <div style={{ fontSize: 11, color: COLORS.text, wordBreak: "break-all" }}>{v}</div>;
  }
  // Object / array → recurse (capped depth via overflow)
  return (
    <div style={{
      borderLeft: `2px solid ${COLORS.border}`, paddingLeft: 8, marginLeft: 2,
    }}>
      <JsonPayload data={v} />
    </div>
  );
}

// SmartAttributes — like SmartPayload but with a "raw" toggle for
// power users who want to see the OTEL attribute bag as JSON.
function SmartAttributes({ attrs }) {
  const [showRaw, setShowRaw] = React.useState(false);
  // Drop noise keys already surfaced elsewhere in the panel
  const noise = new Set([
    "langfuse.session.id", "langfuse.user.id", "langfuse.tags",
    "tenant_id", "user_id", "customer_profile_id", "agent_id",
    "input.value", "input.mime_type",
    "output.value", "output.mime_type",
    "output.state_keys",
  ]);
  const filtered = Object.fromEntries(
    Object.entries(attrs).filter(([k]) => !noise.has(k))
  );
  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
        <button
          onClick={() => setShowRaw(!showRaw)}
          style={{
            background: "none", border: "none",
            color: COLORS.accentText || COLORS.accent || "#2563eb",
            cursor: "pointer", fontSize: 10, fontWeight: 600,
          }}
        >
          {showRaw ? "▴ structured" : "▾ raw JSON"}
        </button>
      </div>
      {showRaw
        ? <Code>{JSON.stringify(attrs, null, 2)}</Code>
        : Object.keys(filtered).length > 0
          ? <JsonPayload data={filtered} />
          : <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: "italic" }}>
              (all attributes already shown above; click ▾ raw JSON to see the full bag)
            </div>
      }
    </>
  );
}


// ─── Helpers ──────────────────────────────────────────────────────

function flattenSpansForTree(spans) {
  // Sort by started_at, compute depth from parent_id chain. Keep order
  // stable so the timeline reads top-to-bottom in execution order.
  const byId = new Map(spans.map((s) => [s.id, s]));
  const depthOf = (s, seen = new Set()) => {
    if (!s.parent_id || !byId.has(s.parent_id) || seen.has(s.id)) return 0;
    seen.add(s.id);
    return 1 + depthOf(byId.get(s.parent_id), seen);
  };
  return spans
    .map((s) => ({ ...s, depth: depthOf(s) }))
    .sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
}

function formatDur(ms) {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1) return `${ms.toFixed(2)}ms`;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function Centered({ children }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      height: "calc(100vh - 48px)", gap: 12, background: COLORS.bg,
    }}>{children}</div>
  );
}

function Pill({ bg, fg, children }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 7px", borderRadius: 999,
      fontSize: 10, fontWeight: 600, background: bg, color: fg, marginRight: 4,
    }}>{children}</span>
  );
}

function Avatar({ name }) {
  const initial = (name || "?")[0].toUpperCase();
  return (
    <div style={{
      width: 36, height: 36, borderRadius: "50%",
      background: "linear-gradient(135deg, #7c3aed, #2563eb)",
      color: "white", display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: 14,
    }}>{initial}</div>
  );
}

function KV({ label, value }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "70px 1fr", gap: 5,
                  fontSize: 11, color: COLORS.text, marginTop: 6 }}>
      <span style={{ color: COLORS.textMuted }}>{label}</span>
      <span style={{ wordBreak: "break-all" }}>{value}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 9, color: COLORS.textMuted,
                    textTransform: "uppercase", fontWeight: 700,
                    letterSpacing: 0.4, marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

function Code({ children }) {
  return (
    <pre style={{
      background: COLORS.surfaceMuted || COLORS.bg,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 4, padding: 8, fontSize: 10, color: COLORS.text,
      whiteSpace: "pre-wrap", wordBreak: "break-all", margin: 0,
      fontFamily: "monospace", maxHeight: 250, overflow: "auto",
    }}>{children}</pre>
  );
}

function navLink() {
  return {
    background: "none", border: "none",
    color: COLORS.accentText || COLORS.accent || "#2563eb",
    cursor: "pointer", fontSize: 12, fontWeight: 600,
  };
}

function btn(variant) {
  const isPrimary = variant === "primary";
  return {
    padding: "5px 11px", fontSize: 11, fontWeight: 600, borderRadius: 5,
    background: isPrimary ? (COLORS.accent || "#18181b") : COLORS.surface,
    color: isPrimary ? "#fff" : COLORS.text,
    border: isPrimary ? "none" : `1px solid ${COLORS.border}`,
    cursor: "pointer",
  };
}
